import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate, useLocation, useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Empty,
  Form,
  Input,
  InputNumber,
  Radio,
  Segmented,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  Flex,
} from 'antd';
import { SearchOutlined, LinkOutlined } from '@ant-design/icons';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { PERMISSIONS } from '../utils/permissions';
import MagicLinkConfirmationModal from './MagicLinkConfirmationModal';
import ResponsiveModal from './workspaceInformation/ResponsiveModal';
import './workspaceInformation/wiPhase1.css';
import {
  MAX_OVERDRAFT_LIMIT,
  validateStripePaymentLink,
} from '../utils/stripePaymentLink';

function formatStripeUnixSeconds(ts) {
  if (ts == null || ts === '') return '—';
  const n = typeof ts === 'number' ? ts : Number(ts);
  if (!Number.isFinite(n) || n <= 0) return '—';
  try {
    return new Date(n * 1000).toLocaleString();
  } catch {
    return '—';
  }
}

/** Local YYYY-MM-DD for date inputs; months from today (1y = 12). */
function expiryDateMonthsFromToday(months) {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setMonth(d.getMonth() + months);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

const CREDITS_EXPIRY_PRESETS = [
  { label: '1 month', months: 1 },
  { label: '3 months', months: 3 },
  { label: '6 months', months: 6 },
  { label: '1 year', months: 12 },
];

const { Title, Text, Paragraph } = Typography;

const WorkspaceInformation = () => {
  const { theme } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const slugFromQuery = searchParams.get('slug');
  const { user, hasPermission } = useAuth();
  const [searchType, setSearchType] = useState('slug');
  const [searchValue, setSearchValue] = useState('');
  const [environment, setEnvironment] = useState('production');
  const [workspaceInfo, setWorkspaceInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showAddCreditsModal, setShowAddCreditsModal] = useState(false);
  const [creditsToAdd, setCreditsToAdd] = useState('');
  const [creditDescription, setCreditDescription] = useState('');
  const [creditsExpiry, setCreditsExpiry] = useState('');
  const [creditsGrantType, setCreditsGrantType] = useState('all-at-once');
  const [bonusReason, setBonusReason] = useState('');
  const [srivatsanApproved, setSrivatsanApproved] = useState('no');
  const [stripePaymentLink, setStripePaymentLink] = useState('');
  const [overdraftLimitInput, setOverdraftLimitInput] = useState('');
  const [resetOverdraftToDefault, setResetOverdraftToDefault] = useState(false);
  /** Add-credits modal top mode: grant credits (Purchased/Bonus) vs overdraft only */
  const [creditsModalMode, setCreditsModalMode] = useState('purchased');
  const [addingCredits, setAddingCredits] = useState(false);
  const [addCreditsError, setAddCreditsError] = useState('');
  const [addCreditsSuccess, setAddCreditsSuccess] = useState('');
  const [showDeductCreditsModal, setShowDeductCreditsModal] = useState(false);
  const [creditsToDeduct, setCreditsToDeduct] = useState('');
  const [deductingCredits, setDeductingCredits] = useState(false);
  const [deductCreditsError, setDeductCreditsError] = useState('');
  const [deductCreditsSuccess, setDeductCreditsSuccess] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const suggestionsRef = useRef(null);
  const inputRef = useRef(null);
  const segmentsSectionRef = useRef(null);
  const location = useLocation();
  const params = useParams();
  const [workspaceUsers, setWorkspaceUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userSSOStates, setUserSSOStates] = useState({});
  const [magicLinkStates, setMagicLinkStates] = useState({});
  const [showMagicLinkModal, setShowMagicLinkModal] = useState(false);
  const [magicLinkModalEmail, setMagicLinkModalEmail] = useState('');
  const [magicLinkModalEnvironment, setMagicLinkModalEnvironment] = useState('');
  const [selectedPlan, setSelectedPlan] = useState('');
  const [selectedUserEmail, setSelectedUserEmail] = useState('');
  const [generatingLink, setGeneratingLink] = useState(false);
  const [upgradeLink, setUpgradeLink] = useState('');
  const [upgradeLinkError, setUpgradeLinkError] = useState('');
  const [showChangeSubscriptionModal, setShowChangeSubscriptionModal] = useState(false);
  const [stripeSubscriptionLink, setStripeSubscriptionLink] = useState('');
  const [subscriptionLinkError, setSubscriptionLinkError] = useState('');
  const [changingSubscription, setChangingSubscription] = useState(false);
  const [changeSubscriptionSuccess, setChangeSubscriptionSuccess] = useState('');
  const [changeSubscriptionError, setChangeSubscriptionError] = useState('');
  const [selectedUserEmailForSubscription, setSelectedUserEmailForSubscription] = useState('');
  const [allowingFreePlan, setAllowingFreePlan] = useState(false);
  const [allowFreePlanError, setAllowFreePlanError] = useState('');
  const [allowFreePlanSuccess, setAllowFreePlanSuccess] = useState('');
  const [companyProfile, setCompanyProfile] = useState('');
  const [researchDomain, setResearchDomain] = useState('');
  const [companyLoading, setCompanyLoading] = useState(false);
  const [companyError, setCompanyError] = useState('');
  const [emailNotification, setEmailNotification] = useState(null);
  const [platformUsage, setPlatformUsage] = useState(null);
  const [platformUsageLoading, setPlatformUsageLoading] = useState(false);
  const [platformUsageError, setPlatformUsageError] = useState('');
  const [expanded, setExpanded] = useState({ icps: false, signals: false, tasks: false, activeUsers: false, icpConditions: {}, signalConditions: {}, });
  const [showActiveUsers, setShowActiveUsers] = useState(false);
  const [adoptionSnapshot, setAdoptionSnapshot] = useState(null);
  const [addingType, setAddingType] = useState(null);
  const [newItemData, setNewItemData] = useState({});
  const [isSavingResearch, setIsSavingResearch] = useState(false);
  const [adoptionLoading, setAdoptionLoading] = useState(false);
  const [adoptionError, setAdoptionError] = useState('');
  const [showActiveUsersOnly, setShowActiveUsersOnly] = useState(false);
  const [segments, setSegments] = useState([]);
  const [segmentsLoading, setSegmentsLoading] = useState(false);
  const [segmentsError, setSegmentsError] = useState('');
  const [segmentFillRateStates, setSegmentFillRateStates] = useState({});
  const [showSourcesModal, setShowSourcesModal] = useState(false);
  const [activeSegment, setActiveSegment] = useState(null);
  const [searchHistory, setSearchHistory] = useState([]);
  const [segmentSortKey, setSegmentSortKey] = useState('systemUpdatedAt');
  const [lifecycleScore, setLifecycleScore] = useState(null);
  const [nextBestAction, setNextBestAction] = useState(null);
  const [lifecycleScoreLoading, setLifecycleScoreLoading] = useState(false);
  const lifecycleDataFetchedRef = useRef(false);
  const [isEditingOwners, setIsEditingOwners] = useState(false);
  const [tempSalesOwner, setTempSalesOwner] = useState("");
  const [tempOnboardingOwner, setTempOnboardingOwner] = useState("");
  const [isSavingOwners, setIsSavingOwners] = useState(false);
  const [adminUsers, setAdminUsers] = useState([]);
  // const [taskRuns, setTaskRuns] = useState([]);
  // const [taskRunsLoading, setTaskRunsLoading] = useState(false);
  // const [taskRunsError, setTaskRunsError] = useState('');

  const toggleIcpCondition = (idx) => {
    setExpanded((e) => ({ ...e, icpConditions: { ...(e.icpConditions || {}), [idx]: !e.icpConditions?.[idx] } }));
  };

  const toggleSignalCondition = (idx) => {
    setExpanded((e) => ({ ...e, signalConditions: { ...(e.signalConditions || {}), [idx]: !e.signalConditions?.[idx] } }));
  };

  // Compact, expandable filters list component
  const FiltersList = ({ filters = [], collapsedLimit = 6 }) => {
    const [expandedAll, setExpandedAll] = useState(false);
    if (!Array.isArray(filters) || filters.length === 0) return <div style={{ color: '#6c757d', fontStyle: 'italic' }}>No filters</div>;

    const visible = expandedAll ? filters : filters.slice(0, collapsedLimit);

    return (
      <div style={{ marginTop: '8px' }}>
        <div style={{ maxHeight: expandedAll ? 'none' : '260px', overflowY: expandedAll ? 'visible' : 'auto', paddingRight: '6px' }}>
          {visible.map((f, i) => (
            <div key={i} style={{ padding: '6px 8px', borderRadius: '6px', background: '#f8f9fa', marginBottom: '6px', fontSize: '13px' }}>
              <div><strong>Field:</strong> {f.field || f.field_name || f.column || '-'}</div>
              <div><strong>Operator:</strong> {f.operator || f.op || '-'}</div>
              <div><strong>Values:</strong> {Array.isArray(f.values) ? f.values.join(', ') : (f.values || '-')}</div>
            </div>
          ))}
        </div>
        {filters.length > collapsedLimit && (
          <div style={{ marginTop: '6px' }}>
            <button onClick={() => setExpandedAll(!expandedAll)} style={{ background: 'none', border: 'none', color: '#007bff', cursor: 'pointer', padding: 0 }}>
              {expandedAll ? `Show less` : `Show all (${filters.length})`}
            </button>
          </div>
        )}
      </div>
    );
  };
  const publicEmailDomains = [
    "gmail.com", "yahoo.com", "yahoo.in", "ymail.com", "rocketmail.com",
    "outlook.com", "hotmail.com", "live.com", "msn.com", "icloud.com",
    "me.com", "mac.com", "aol.com", "protonmail.com", "proton.me",
    "zoho.com", "zohomail.com", "gmx.com", "gmx.net", "mail.com",
    "yandex.com", "yandex.ru", "rediffmail.com", "lycos.com",
    "tutanota.com", "fastmail.com", "yahoo.co.uk", "hotmail.co.uk",
    "live.co.uk", "163.com", "126.com", "qq.com", "naver.com",
    "daum.net", "highperformr.ai"
  ];

  function extractCompanyDomain(email) {
    if (!email || !email.includes("@")) return null;
    const domain = email.split("@")[1].toLowerCase();
    if (publicEmailDomains.includes(domain)) return null;
    return domain;
  }

  useEffect(() => {
    fetchAdminUsers();
  }, []);

  const fetchAdminUsers = async () => {
    try {
      const response = await api.get('/api/users');
      setAdminUsers(response.data.data || []);
    } catch (err) {
      console.error('Error fetching admin users:', err);
    }
  };

  const fetchWorkspaceInfo = async (type, value, environment = null) => {
    if (!value) return;
    setLoading(true);
    setError('');
    setWorkspaceInfo(null);
    setWorkspaceUsers([]);
    setUserSSOStates({});
    setPlatformUsage(null);
    setCompanyProfile(null);
    setResearchDomain('');
    setAdoptionSnapshot(null);
    setAllowFreePlanError('');
    setAllowFreePlanSuccess('');

    try {
      let queryParams = `searchType=${encodeURIComponent(type)}`;

      if (type === 'slug') {
        queryParams += `&slug=${encodeURIComponent(value)}`;
        // Use provided environment parameter, or fallback to component's environment state
        const envToUse = environment !== null && environment !== undefined ? environment : 'production';
        queryParams += `&environment=${encodeURIComponent(envToUse)}`;
      } else if (type === 'stripeCustomerId') {
        queryParams += `&stripeCustomerId=${encodeURIComponent(value)}`;
      } else if (type === 'stripeSubscriptionId') {
        queryParams += `&stripeSubscriptionId=${encodeURIComponent(value)}`;
      }

      const response = await api.get(`/api/workspace/info?${queryParams}`);
      const data = response.data.data;
      setWorkspaceInfo(data);
      setSelectedUserEmail('');
      setUpgradeLink('');
      setUpgradeLinkError('');

      // Fetch users for this workspace
      if (data?.authAccountId && data?.environment) {
        fetchWorkspaceUsers(data.authAccountId, data.environment);
      }

      // Add to search history
      const historyEntry = { type, value, timestamp: Date.now() };
      const envForHistory = environment || data?.environment;
      if (type === 'slug' && envForHistory) {
        historyEntry.environment = envForHistory;
      }
      const newHistory = [
        historyEntry,
        ...searchHistory.filter(h => h.value !== value || h.type !== type || (h.environment && h.environment !== envForHistory))
      ].slice(0, 10);
      setSearchHistory(newHistory);
      localStorage.setItem('workspaceSearchHistory', JSON.stringify(newHistory));
    } catch (err) {
      console.error('Error fetching workspace info:', err);
      if (err.response?.status === 404) {
        setError('Workspace not found');
      } else {
        setError(err.response?.data?.error || 'Failed to fetch workspace information');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleWorkspaceSearch = async (e) => {
    e.preventDefault();
    if (!searchValue.trim()) return;

    // Update URL with query parameter if searching by slug
    if (searchType === 'slug') {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('slug', searchValue);
      setSearchParams(newSearchParams, { replace: true });
    } else {
      // Remove slug from URL if searching by other type
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('slug');
      setSearchParams(newSearchParams, { replace: true });
    }

    // Use the environment toggle value when searching
    await fetchWorkspaceInfo(searchType, searchValue, environment);
  };

  const handleAddClick = (type) => {
    setAddingType(type);
    setNewItemData({
      filters: [],
      condition_match_logic: type === 'icps' ? 'ALL' : 'ANY'
    });
  };

  const handleCancelAdd = () => {
    setAddingType(null);
    setNewItemData({});
  };

  const handleSaveAdd = async () => {
    if (!addingType) return;
    setIsSavingResearch(true);
    try {
      let updatedList = [];
      let fieldName = '';

      if (addingType === 'competitors') {
        fieldName = 'competitors';
        const current = companyProfile.competitors || [];
        updatedList = [...current, { name: newItemData.name, domain: newItemData.domain, reason: '' }];
      } else if (addingType === 'icps') {
        fieldName = 'suggested_icps';
        const current = companyProfile.suggested_icps || [];
        updatedList = [...current, {
          name: newItemData.name,
          description: newItemData.description || '',
          filters: newItemData.filters || [],
          condition_match_logic: newItemData.condition_match_logic || 'ALL',
        }];
      } else if (addingType === 'signals') {
        fieldName = 'suggested_signals';
        const current = companyProfile.suggested_signals || [];
        updatedList = [...current, {
          name: newItemData.name,
          description: newItemData.description || '',
          filters: newItemData.filters || [],
          condition_match_logic: newItemData.condition_match_logic || 'ANY',
        }];
      }

      const enrichment = {
        [fieldName]: updatedList
      };

      await api.post(`/api/workspace/${workspaceInfo.accountId}/research-data`, {
        domain: researchDomain,
        linkedin_url: companyProfile.linkedin_url,
        industry: companyProfile.industry,
        enrichment
      });

      setCompanyProfile({ ...companyProfile, ...enrichment });
      setAddingType(null);
      setNewItemData({});
    } catch (error) {
      console.error("Failed to save research data:", error);
      setCompanyError("Failed to save research data");
    } finally {
      setIsSavingResearch(false);
    }
  };

  useEffect(() => {
    if (slugFromQuery) {
      setSearchType('slug');
      setSearchValue(slugFromQuery);
      fetchWorkspaceInfo('slug', slugFromQuery, environment);
    }
  }, [slugFromQuery, environment]);

  useEffect(() => {
    if (slugFromQuery) return;

    const handleWorkspaceSearch = async () => {
      const searchData = sessionStorage.getItem('workspaceSearch');
      if (searchData) {
        try {
          const { searchType: storedSearchType, searchValue: storedSearchValue } = JSON.parse(searchData);
          setSearchType(storedSearchType);
          setSearchValue(storedSearchValue);
          // Clear the stored data
          sessionStorage.removeItem('workspaceSearch');
          // Trigger search after a short delay to ensure state is updated
          if (storedSearchValue) {
            await fetchWorkspaceInfo(storedSearchType, storedSearchValue);
          }
        } catch (err) {
          console.error('Error parsing workspace search data:', err);
        }
      }
    };

    window.addEventListener('workspaceSearchRequested', handleWorkspaceSearch);

    // Check on mount if there's stored search data
    handleWorkspaceSearch();

    return () => {
      window.removeEventListener('workspaceSearchRequested', handleWorkspaceSearch);
    };
  }, [slugFromQuery]);

  useEffect(() => {
    if (workspaceInfo?.accountId) {
      fetchAdoptionSnapshot();
    }
  }, [workspaceInfo?.accountId]);

  useEffect(() => {
    const history = localStorage.getItem('workspaceSearchHistory');
    if (history) {
      try {
        setSearchHistory(JSON.parse(history));
      } catch (e) {
        console.error('Error parsing search history:', e);
      }
    }
  }, []);


  useEffect(() => {
    const fetchPlatformUsage = async () => {
      if (!workspaceInfo?.accountId) return;

      setPlatformUsageLoading(true);
      setPlatformUsageError('');

      try {
        const authAccountId = workspaceInfo.authAccountId || workspaceInfo.auth_account_id;
        if (!authAccountId) {
          setPlatformUsageError('authAccountId is required for platform usage');
          setPlatformUsageLoading(false);
          return;
        }
        const res = await api.get(
          `/api/workspace/${workspaceInfo.accountId}/platform-usage?authAccountId=${authAccountId}&environment=${workspaceInfo.environment || 'production'}`
        );
        setPlatformUsage(res.data.data);
      } catch (err) {
        console.error('Error fetching platform usage:', err);
        setPlatformUsageError(err.response?.data?.message || 'Failed to load platform usage');
      } finally {
        setPlatformUsageLoading(false);
      }
    };

    fetchPlatformUsage();
  }, [workspaceInfo?.accountId]);

  useEffect(() => {
    if (!workspaceInfo?.accountId) return;

    const fetchSegments = async () => {
      setSegmentsLoading(true);
      setSegmentsError('');

      try {
        const res = await api.get(
          `/api/workspace/${workspaceInfo.accountId}/segments-with-sources?environment=${workspaceInfo.environment}`
        );
        setSegments(res.data.data || []);
      } catch (err) {
        console.error(err);
        setSegmentsError('Failed to load segments');
      } finally {
        setSegmentsLoading(false);
      }
    };

    fetchSegments();
  }, [workspaceInfo?.accountId]);

  useEffect(() => {
    if (!workspaceInfo) return;
    const section = searchParams?.get?.('section') || searchParams?.get?.('scrollTo');
    const shouldScrollToSegments =
      (location?.hash === '#segments') ||
      (section === 'segments') ||
      (typeof location?.pathname === 'string' && location.pathname.endsWith('/segments')) ||
      (params && params.slug && typeof location?.pathname === 'string' && location.pathname.includes(`/workspace-info/${params.slug}/segments`));

    if (!shouldScrollToSegments) return;

    // Wait a tick so the section exists in the DOM (and data fetch can kick off)
    setTimeout(() => {
      segmentsSectionRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    }, 0);
  }, [workspaceInfo, segmentsLoading, segments.length, location?.hash, location?.pathname, params?.slug, searchParams]);

  const formatRate = (r) => {
    if (!r || typeof r.rate !== 'number') return '—';
    return `${(r.rate * 100).toFixed(1)}%`;
  };

  const generateFillRateForSegmentField = async (segment, key) => {
    if (!workspaceInfo?.accountId || !segment?.segmentId) return;

    // If any generate is clicked inside a segment, generate all fill rates for that segment.
    const segmentKeys = [
      'companyWebsite',
      'email',
      'phoneNumber',
      'linkedinProfileUrlPerson',
      'firstName',
      'lastName',
      'linkedinLastActiveDate',
      'companyLinkedinProfile'
    ];
    const stateKeys = segmentKeys.map((k) => `${segment.segmentId}:${k}`);
    setSegmentFillRateStates((s) => {
      const next = { ...s };
      stateKeys.forEach((sk) => { next[sk] = { loading: true, error: '' }; });
      return next;
    });

    try {
      const payload = {
        sourceIds: (segment.sources || []).map((x) => x.id).filter(Boolean)
      };
      const res = await api.post(
        `/api/workspace/${workspaceInfo.accountId}/segments/${segment.segmentId}/fill-rates?environment=${workspaceInfo.environment || 'production'}`,
        payload
      );
      const data = res?.data?.data || null;

      setSegments((prev) =>
        prev.map((seg) =>
          seg.segmentId === segment.segmentId
            ? {
              ...seg,
              fillRates: {
                ...(seg.fillRates || {}),
                ...(data || {}),
                rates: {
                  ...((seg.fillRates && seg.fillRates.rates) || {}),
                  ...((data && data.rates) || {})
                }
              }
            }
            : seg
        )
      );
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to generate fill rates';
      setSegmentFillRateStates((s) => {
        const next = { ...s };
        stateKeys.forEach((sk) => { next[sk] = { loading: false, error: msg }; });
        return next;
      });
    } finally {
      setSegmentFillRateStates((s) => {
        const next = { ...s };
        stateKeys.forEach((sk) => { next[sk] = { ...(next[sk] || {}), loading: false }; });
        return next;
      });
    }
  };

  useEffect(() => {
    const fetchLifecycleData = async () => {
      if (!adoptionSnapshot || !platformUsage || !workspaceInfo) {
        setLifecycleScore(null);
        setNextBestAction(null);
        lifecycleDataFetchedRef.current = false;
        return;
      }

      // Prevent duplicate calls if already loading or already fetched for this data
      const dataKey = `${workspaceInfo?.accountId}-${adoptionSnapshot?.data?.setup?.contacts?.total || 0}-${platformUsage?.contacts?.total || 0}`;
      if (lifecycleScoreLoading || lifecycleDataFetchedRef.current === dataKey) {
        return;
      }

      setLifecycleScoreLoading(true);
      try {
        // Calculate lifecycle score
        const scoreData = await calculateLifecycleScore();
        if (scoreData && scoreData.score !== undefined) {
          setLifecycleScore(scoreData);

          // Calculate next best action (it needs the score)
          const action = await calculateNextBestAction(scoreData.score);
          if (action) {
            setNextBestAction(action);
          }
          lifecycleDataFetchedRef.current = dataKey;
        } else {
        }
      } catch (err) {
        console.error('Error fetching lifecycle data:', err);
      } finally {
        setLifecycleScoreLoading(false);
      }
    };

    fetchLifecycleData();
  }, [adoptionSnapshot?.data, platformUsage?.contacts?.total, workspaceInfo?.accountId]);

  const getScoreColor = (meaning) => {
    if (meaning === "Setup incomplete") return '#dc3545'; // Red
    if (meaning === "Configured but not active") return '#ffc107'; // Yellow
    if (meaning === "Active but not habitual") return '#17a2b8'; // Blue
    if (meaning === "Regular usage") return '#28a745'; // Green
    if (meaning === "Fully adopted") return '#28a745'; // Green
    return '#6c757d'; // Default gray
  };

  const getScoreBackgroundColor = (meaning) => {
    if (meaning === "Setup incomplete") return '#f8d7da';
    if (meaning === "Configured but not active") return '#fff3cd';
    if (meaning === "Active but not habitual") return '#d1ecf1';
    if (meaning === "Regular usage") return '#d4edda';
    if (meaning === "Fully adopted") return '#d4edda';
    return '#e9ecef';
  };


  // useEffect(() => {
  //   if (!workspaceInfo?.accountId) return;

  //   const fetchTaskRuns = async () => {
  //     setTaskRunsLoading(true);
  //     setTaskRunsError('');

  //     try {
  //       const res = await api.get(
  //         `/api/workspace/${workspaceInfo.accountId}/task-runs?environment=${workspaceInfo.environment}`
  //       );
  //       setTaskRuns(res.data.data?.tasks || []);
  //     } catch (err) {
  //       console.error(err);
  //       setTaskRunsError('Failed to load enrichments');
  //     } finally {
  //       setTaskRunsLoading(false);
  //     }
  //   };

  //   fetchTaskRuns();
  // }, [workspaceInfo?.accountId]);

  const saveWorkspaceDomain = async (accountId, domain) => {
    try {
      await api.post(`/api/workspace/${accountId}/save-domain`, {
        domain: domain
      });
    } catch (error) {
      console.error('Failed to save domain to cache:', error);
    }
  };



  const handleSaveOwners = async () => {
    if (!workspaceInfo?.accountId) return;
    setIsSavingOwners(true);
    try {
      await api.post(`/api/workspace/${workspaceInfo.accountId}/save-domain`, {
        domain: workspaceInfo.cachedDomain || "",
        salesOwner: tempSalesOwner,
        onboardingOwner: tempOnboardingOwner
      });

      setWorkspaceInfo({
        ...workspaceInfo,
        salesOwner: tempSalesOwner,
        onboardingOwner: tempOnboardingOwner
      });
      setIsEditingOwners(false);
    } catch (error) {
      console.error('Failed to save owners:', error);
      alert('Failed to save owners');
    } finally {
      setIsSavingOwners(false);
    }
  };

  const handleSearch = async () => {
    if (!searchValue.trim()) {
      const fieldName = searchType === 'slug' ? 'workspace slug' :
        searchType === 'stripeCustomerId' ? 'Stripe Customer ID' :
          'Stripe Subscription ID';
      setError(`Please enter a ${fieldName}`);
      return;
    }
    await fetchWorkspaceInfo(searchType, searchValue);
  };

  const calculateLifecycleScore = async () => {
    if (!adoptionSnapshot || !platformUsage) return;

    try {
      // Extract data from already-fetched responses
      const totalContacts = platformUsage?.contacts?.total || 0;
      const totalIcps = platformUsage?.icps?.totalIcps || 0;
      const totalSignals = platformUsage?.signals?.totalSignals || 0;
      const totalTasksConfigured = platformUsage?.tasks?.totalTasksMade || 0;
      const tasksConfiguredLast14d = platformUsage?.tasks?.totalTasksMadeLastWeek || 0; // This will be 14 days after update
      const enrichmentsRunLast14d = adoptionSnapshot?.activation?.enrichment_run?.count_last_14_days || 0;
      const automationEverEnabled = adoptionSnapshot?.automation?.workflow_automated || false;
      const automationRunsLast14d = adoptionSnapshot?.automation?.automation_runs_last_14_days || 0;
      const activeUsers14d = platformUsage?.users?.active || 0;
      const totalUsers = platformUsage?.users?.total || 0;

      const queryParams = new URLSearchParams({
        totalContacts: totalContacts.toString(),
        totalIcps: totalIcps.toString(),
        totalSignals: totalSignals.toString(),
        totalTasksConfigured: totalTasksConfigured.toString(),
        tasksConfiguredLast14d: tasksConfiguredLast14d.toString(),
        enrichmentsRunLast14d: enrichmentsRunLast14d.toString(),
        automationEverEnabled: automationEverEnabled.toString(),
        automationRunsLast14d: automationRunsLast14d.toString(),
        activeUsers14d: activeUsers14d.toString(),
        totalUsers: totalUsers.toString()
      });

      const res = await api.get(`/api/workspace/lifecycle-adoption-score?${queryParams.toString()}`);

      return res.data.data;
    } catch (err) {
      console.error('Error calculating lifecycle score:', err);
      return null;
    }
  };
  const calculateNextBestAction = async (lifecycleScoreValue = 0) => {
    if (!adoptionSnapshot || !platformUsage || !workspaceInfo) return;

    try {
      // Extract data from already-fetched responses
      const availableCredits = workspaceInfo?.availableCredits || 0;
      const totalContacts = platformUsage?.contacts?.total || 0;
      const totalIcps = platformUsage?.icps?.totalIcps || 0;
      const totalSignals = platformUsage?.signals?.totalSignals || 0;
      const totalTasksConfigured = platformUsage?.tasks?.totalTasksMade || 0;
      const enrichmentsRunLast14d = adoptionSnapshot?.activation?.enrichment_run?.count_last_14_days || 0;
      const automationEverEnabled = adoptionSnapshot?.automation?.workflow_automated || false;
      const automationRunsLast14d = adoptionSnapshot?.automation?.automation_runs_last_14_days || 0;
      const activeUsers14d = platformUsage?.users?.active || 0;
      const totalUsers = platformUsage?.users?.total || 0;

      // Get next best action using the lifecycle score passed as parameter
      const queryParams = new URLSearchParams({
        availableCredits: availableCredits.toString(),
        totalContacts: totalContacts.toString(),
        totalIcps: totalIcps.toString(),
        totalSignals: totalSignals.toString(),
        totalTasksConfigured: totalTasksConfigured.toString(),
        tasksConfiguredLast14d: (platformUsage?.tasks?.totalTasksMadeLastWeek || 0).toString(),
        enrichmentsRunLast14d: enrichmentsRunLast14d.toString(),
        automationEverEnabled: automationEverEnabled.toString(),
        automationRunsLast14d: automationRunsLast14d.toString(),
        activeUsers14d: activeUsers14d.toString(),
        totalUsers: totalUsers.toString(),
        lifecycleAdoptionScore: lifecycleScoreValue.toString()
      });

      const res = await api.get(`/api/workspace/next-best-action?${queryParams.toString()}`);

      return res.data.data.action;
    } catch (err) {
      console.error('Error calculating next best action:', err);
      return null;
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      setShowSuggestions(false);
      handleSearch();
    }
  };

  // Debounced function to fetch suggestions (only for slug search)
  useEffect(() => {
    const fetchSuggestions = async () => {
      if (searchType !== 'slug' || !searchValue.trim() || searchValue.trim().length < 2) {
        setSuggestions([]);
        setShowSuggestions(false);
        return;
      }

      setLoadingSuggestions(true);
      try {
        // Pass environment parameter to suggestions API
        const response = await api.get(`/api/workspace/suggestions?query=${encodeURIComponent(searchValue)}&limit=10&environment=${encodeURIComponent(environment)}`);
        setSuggestions(response.data.data || []);
        setShowSuggestions(true);
      } catch (err) {
        console.error('Error fetching suggestions:', err);
        setSuggestions([]);
      } finally {
        setLoadingSuggestions(false);
      }
    };

    const timeoutId = setTimeout(fetchSuggestions, 300); // 300ms debounce
    return () => clearTimeout(timeoutId);
  }, [searchValue, searchType, environment]); // Include environment in dependencies

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSuggestionClick = async (suggestion) => {
    setSearchValue(suggestion.slug);
    setShowSuggestions(false);

    // Update URL with slug query parameter
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('slug', suggestion.slug);
    setSearchParams(newSearchParams, { replace: true });

    // Automatically fetch workspace info when clicking a suggestion
    // Pass environment to ensure we get the correct workspace (especially when same slug exists in multiple environments)
    await fetchWorkspaceInfo('slug', suggestion.slug, suggestion.environment);
  };

  const handleInputChange = (e) => {
    setSearchValue(e.target.value);
    setError('');
  };

  const handleSearchTypeChange = (valueOrEvent) => {
    const nextType = typeof valueOrEvent === 'string'
      ? valueOrEvent
      : valueOrEvent?.target?.value;
    setSearchType(nextType);
    setSearchValue('');
    setSuggestions([]);
    setShowSuggestions(false);
    setError('');
  };

  const getPlaceholder = () => {
    if (searchType === 'slug') {
      return 'Enter workspace slug (e.g., acme-corp)';
    } else if (searchType === 'stripeCustomerId') {
      return 'Enter Stripe Customer ID (e.g., cus_xxxxx)';
    } else if (searchType === 'stripeSubscriptionId') {
      return 'Enter Stripe Subscription ID (e.g., sub_xxxxx)';
    }
    return 'Enter search value';
  };

  const getStatusColor = (status) => {
    const statusLower = status?.toLowerCase();
    if (statusLower === 'active') return 'success';
    if (statusLower === 'canceled' || statusLower === 'cancelled') return 'error';
    if (statusLower === 'trialing') return 'warning';
    return 'default';
  };

  const closeAddCreditsModal = () => {
    if (addingCredits) return;
    setShowAddCreditsModal(false);
    setAddCreditsError('');
    setAddCreditsSuccess('');
  };

  const openAddCreditsModal = () => {
    const snapshot = workspaceInfo?.creditsSnapshot || {};
    setShowAddCreditsModal(true);
    setCreditsModalMode('purchased');
    setCreditsToAdd('');
    setCreditDescription('Purchased');
    setCreditsExpiry('');
    setCreditsGrantType('all-at-once');
    setBonusReason('');
    setSrivatsanApproved('no');
    setStripePaymentLink('');
    setOverdraftLimitInput(String(Math.max(0, Number(snapshot.overdraftLimit) || 0)));
    setResetOverdraftToDefault(false);
    setAddCreditsError('');
    setAddCreditsSuccess('');
  };

  const currentOverdraftLimit = Math.max(0, Number(workspaceInfo?.creditsSnapshot?.overdraftLimit) || 0);
  const formulaOverdraftLimit = Math.max(0, Number(workspaceInfo?.creditsSnapshot?.formulaOverdraftLimit) || 0);
  const parsedOverdraftInput = Math.floor(Number(overdraftLimitInput));
  const grantTypeSelected =
    creditDescription === 'Bonus credits' || creditDescription === 'Purchased';
  const overdraftDirty =
    resetOverdraftToDefault ||
    (Number.isFinite(parsedOverdraftInput) && parsedOverdraftInput !== currentOverdraftLimit);
  const grantReady =
    creditDescription === 'Bonus credits'
      ? Boolean(bonusReason.trim()) && srivatsanApproved === 'yes' && Number(creditsToAdd) > 0
      : creditDescription === 'Purchased'
        ? validateStripePaymentLink(stripePaymentLink).valid && Number(creditsToAdd) > 0
        : false;
  const canSaveCreditsModal =
    creditsModalMode === 'overdraft'
      ? overdraftDirty
      : grantReady;

  const handleDeductCredits = async () => {
    if (!creditsToDeduct || creditsToDeduct <= 0) {
      setDeductCreditsError('Please enter a valid credits amount');
      return;
    }

    setDeductingCredits(true);
    setDeductCreditsError('');
    setDeductCreditsSuccess('');

    try {
      const response = await api.post('/api/workspace/deduct-credits', {
        authAccountId: workspaceInfo.authAccountId,
        credits: Number(creditsToDeduct),
        environment: workspaceInfo.environment,
        workspaceSlug: workspaceInfo.accountSlug
      });

      setDeductCreditsSuccess(response.data.message || 'Credits deducted successfully');
      setCreditsToDeduct('');

      // Refresh workspace info after deducting credits
      setTimeout(async () => {
        try {
          let queryParams = `searchType=${encodeURIComponent(searchType)}`;

          if (searchType === 'slug') {
            queryParams += `&slug=${encodeURIComponent(searchValue)}`;
          } else if (searchType === 'stripeCustomerId') {
            queryParams += `&stripeCustomerId=${encodeURIComponent(searchValue)}`;
          } else if (searchType === 'stripeSubscriptionId') {
            queryParams += `&stripeSubscriptionId=${encodeURIComponent(searchValue)}`;
          }

          const refreshResponse = await api.get(`/api/workspace/info?${queryParams}`);
          setWorkspaceInfo(refreshResponse.data.data);
          setShowDeductCreditsModal(false);
          setDeductCreditsSuccess('');
        } catch (err) {
          console.error('Error refreshing workspace info:', err);
        }
      }, 2000);
    } catch (err) {
      console.error('Error deducting credits:', err);
      setDeductCreditsError(err.response?.data?.error || 'Failed to deduct credits');
    } finally {
      setDeductingCredits(false);
    }
  };

  const openDeductCreditsModal = () => {
    setShowDeductCreditsModal(true);
    setCreditsToDeduct('');
    setDeductCreditsError('');
    setDeductCreditsSuccess('');
  };

  const refreshWorkspaceInfo = async () => {
    let queryParams = `searchType=${encodeURIComponent(searchType)}`;
    if (searchType === 'slug') {
      queryParams += `&slug=${encodeURIComponent(searchValue)}`;
    } else if (searchType === 'stripeCustomerId') {
      queryParams += `&stripeCustomerId=${encodeURIComponent(searchValue)}`;
    } else if (searchType === 'stripeSubscriptionId') {
      queryParams += `&stripeSubscriptionId=${encodeURIComponent(searchValue)}`;
    }
    const refreshResponse = await api.get(`/api/workspace/info?${queryParams}`);
    setWorkspaceInfo(refreshResponse.data.data);
  };

  const handleSaveCreditsModal = async () => {
    if (!workspaceInfo?.authAccountId) return;

    const snapshot = workspaceInfo.creditsSnapshot || {};
    const currentLimit = Math.max(0, Number(snapshot.overdraftLimit) || 0);
    const savingOverdraft = creditsModalMode === 'overdraft';
    const grantSelected =
      !savingOverdraft &&
      (creditDescription === 'Bonus credits' || creditDescription === 'Purchased');
    const parsedOverdraft = Math.floor(Number(overdraftLimitInput));
    const overdraftIsDirty =
      savingOverdraft &&
      (resetOverdraftToDefault ||
        (Number.isFinite(parsedOverdraft) && parsedOverdraft !== currentLimit));

    if (!overdraftIsDirty && !grantSelected) {
      setAddCreditsError(
        savingOverdraft
          ? 'Change the overdraft limit to save'
          : 'Choose a grant reason and complete the required fields'
      );
      return;
    }

    if (overdraftIsDirty && !resetOverdraftToDefault) {
      if (!Number.isFinite(parsedOverdraft) || parsedOverdraft < 0) {
        setAddCreditsError('Overdraft limit must be a non-negative number');
        return;
      }
      if (parsedOverdraft > MAX_OVERDRAFT_LIMIT) {
        setAddCreditsError(`Overdraft limit cannot exceed ${MAX_OVERDRAFT_LIMIT.toLocaleString()}`);
        return;
      }
    }

    if (grantSelected) {
      if (!creditsToAdd || Number(creditsToAdd) <= 0) {
        setAddCreditsError('Please enter a valid credits amount');
        return;
      }
      if (creditDescription === 'Bonus credits') {
        if (!bonusReason.trim()) {
          setAddCreditsError('Enter why these bonus credits are being granted');
          return;
        }
        if (srivatsanApproved !== 'yes') {
          setAddCreditsError('Bonus credits can only be saved after Srivatsan approval is Yes');
          return;
        }
      } else {
        const stripeValidation = validateStripePaymentLink(stripePaymentLink);
        if (!stripeValidation.valid) {
          setAddCreditsError(stripeValidation.error);
          return;
        }
      }
    }

    setAddingCredits(true);
    setAddCreditsError('');
    setAddCreditsSuccess('');

    try {
      if (overdraftIsDirty) {
        const overrideValue = resetOverdraftToDefault ? null : parsedOverdraft;
        await api.patch('/api/workspace/credits-overdraft', {
          authAccountId: workspaceInfo.authAccountId,
          environment: workspaceInfo.environment,
          workspaceSlug: workspaceInfo.accountSlug,
          overdraftLimitOverride: overrideValue,
        });
      }

      if (grantSelected) {
        const creditsPayload = {
          authAccountId: workspaceInfo.authAccountId,
          credits: Number(creditsToAdd),
          environment: workspaceInfo.environment,
          workspaceSlug: workspaceInfo.accountSlug,
          reason: creditDescription,
          expiresAt: creditsExpiry ? `${creditsExpiry}T00:00:00.000Z` : null,
          isConsumptionBased: creditsGrantType === 'consumption-based',
        };
        if (creditDescription === 'Purchased') {
          creditsPayload.stripePaymentLink = validateStripePaymentLink(stripePaymentLink).url;
        } else {
          creditsPayload.bonusReason = bonusReason.trim();
          creditsPayload.srivatsanApproved = true;
        }
        await api.post('/api/workspace/add-credits', creditsPayload);
      }

      await refreshWorkspaceInfo();
      setShowAddCreditsModal(false);
      setAddCreditsSuccess('');
    } catch (err) {
      console.error('Error saving credits modal:', err);
      setAddCreditsError(err.response?.data?.error || 'Failed to save');
    } finally {
      setAddingCredits(false);
    }
  };

  const fetchWorkspaceUsers = async (authAccountId, environment) => {
    setLoadingUsers(true);
    try {
      const response = await api.get(`/api/workspace/users?authAccountId=${encodeURIComponent(authAccountId)}&environment=${encodeURIComponent(environment)}`);
      setWorkspaceUsers(response.data.data || []);
    } catch (err) {
      console.error('Error fetching workspace users:', err);
      setWorkspaceUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const generateUserSSO = async (userEmail, environment) => {
    const userId = userEmail;
    setUserSSOStates(prev => ({ ...prev, [userId]: { loading: true, error: '', loginUrl: '' } }));

    try {
      const response = await api.post('/api/workspace/user-sso', {
        email: userEmail,
        environment: environment
      });

      setUserSSOStates(prev => ({
        ...prev,
        [userId]: {
          loading: false,
          error: '',
          loginUrl: response.data.data.loginUrl
        }
      }));
    } catch (err) {
      console.error('Error generating SSO login:', err);
      setUserSSOStates(prev => ({
        ...prev,
        [userId]: {
          loading: false,
          error: err.response?.data?.error || 'Failed to generate login link',
          loginUrl: ''
        }
      }));
    }
  };

  const handleMagicLinkClick = (userEmail, environment) => {
    setMagicLinkModalEmail(userEmail);
    setMagicLinkModalEnvironment(environment);
    setShowMagicLinkModal(true);
  };

  const handleMagicLinkConfirm = async () => {
    setShowMagicLinkModal(false);
    const userEmail = magicLinkModalEmail;
    const environment = magicLinkModalEnvironment;
    const userId = userEmail;

    setMagicLinkStates(prev => ({ ...prev, [userId]: { loading: true, error: '', success: false } }));

    try {
      const response = await api.post('/api/workspace/send-magic-link', {
        email: userEmail,
        environment: environment
      });

      setMagicLinkStates(prev => ({
        ...prev,
        [userId]: {
          loading: false,
          error: '',
          success: true
        }
      }));
    } catch (err) {
      console.error('Error sending magic link:', err);
      setMagicLinkStates(prev => ({
        ...prev,
        [userId]: {
          loading: false,
          error: err.response?.data?.error || 'Failed to send magic link',
          success: false
        }
      }));
    } finally {
      setMagicLinkModalEmail('');
      setMagicLinkModalEnvironment('');
    }
  };

  const handleMagicLinkCancel = () => {
    setShowMagicLinkModal(false);
    setMagicLinkModalEmail('');
    setMagicLinkModalEnvironment('');
  };

  const copySSOToClipboard = async (loginUrl, userId) => {
    try {
      await navigator.clipboard.writeText(loginUrl);
      setUserSSOStates(prev => ({
        ...prev,
        [userId]: { ...prev[userId], copied: true }
      }));
      setTimeout(() => {
        setUserSSOStates(prev => ({
          ...prev,
          [userId]: { ...prev[userId], copied: false }
        }));
      }, 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const availablePlans = [
    { value: 'team_gtm_annual_oct25', label: 'Team GTM Annual (Oct 25)' },
    { value: 'team_gtm_annual_200k_oct25', label: 'Team GTM Annual 200K (Oct 25)' },
    { value: 'team_gtm_annual_1M_oct25', label: 'Team GTM Annual 1M (Oct 25)' },
    { value: 'team_gtm_annual_2M_jan26', label: 'Team GTM Annual 2M (Jan 26)' },
    { value: 'team_gtm_half_yearly_oct25', label: 'Team GTM Half Yearly (Oct 25)' },
    { value: 'team_gtm_semi_annual_250k_apr26', label: 'Team GTM Semi-Annual 250K (Apr 26)' },
    { value: 'team_gtm_starter_quarterly_oct26', label: 'Team GTM Starter Quarterly (Oct 26)' },
    { value: 'team_gtm_starter_semi_annual_oct26', label: 'Team GTM Starter Semi-Annual (Oct 26)' },
    { value: 'team_gtm_monthly_oct25', label: 'Team GTM Monthly (Oct 25)' },
    { value: 'team_gtm_campaigns_half_yearly_jan26', label: 'Team GTM Campaigns Half Yearly (Jan 26)' },
    { value: 'team_lifetime_229', label: 'Team Lifetime $229' },
    { value: 'pro_lifetime_59', label: 'Pro Lifetime $59' },
    { value: 'team_monthly', label: 'Team Monthly' },
    { value: 'team_yearly', label: 'Team Yearly' },
    { value: 'team_lifetime', label: 'Team Lifetime' },
  ];

  const handleGenerateUpgradeLink = async () => {
    if (!selectedPlan) {
      setUpgradeLinkError('Please select a plan');
      return;
    }

    if (!selectedUserEmail) {
      setUpgradeLinkError('Please select a user');
      return;
    }

    if (!workspaceInfo?.authAccountId) {
      setUpgradeLinkError('Workspace information is required');
      return;
    }

    if (!workspaceInfo?.accountSlug) {
      setUpgradeLinkError('Account slug is required');
      return;
    }

    setGeneratingLink(true);
    setUpgradeLinkError('');
    setUpgradeLink('');

    try {
      const response = await api.post('/api/workspace/generate-upgrade-link', {
        authAccountId: workspaceInfo.authAccountId,
        accountSlug: workspaceInfo.accountSlug,
        plan: selectedPlan,
        userEmail: selectedUserEmail,
        environment: workspaceInfo.environment
      });

      const checkoutUrl = response.data.data?.checkoutUrl ||
        response.data.data?.url ||
        response.data?.checkoutUrl ||
        response.data?.url ||
        '';
      setUpgradeLink(checkoutUrl);
    } catch (err) {
      console.error('Error generating upgrade link:', err);
      setUpgradeLinkError(err.response?.data?.error || 'Failed to generate upgrade link');
    } finally {
      setGeneratingLink(false);
    }
  };

  const copyUpgradeLinkToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(upgradeLink);
      // Show temporary feedback
      const button = document.getElementById('copy-upgrade-link-btn');
      if (button) {
        const originalText = button.textContent;
        button.textContent = '✓ Copied!';
        button.style.backgroundColor = '#27ae60';
        setTimeout(() => {
          button.textContent = originalText;
          button.style.backgroundColor = '#2c3e50';
        }, 2000);
      }
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const validateStripeSubscriptionLink = (link) => {
    if (!link || !link.trim()) {
      return { valid: false, error: 'Stripe subscription link is required' };
    }

    // Production format: https://dashboard.stripe.com/acct_{accountId}/subscriptions/{subscriptionId}
    // Staging format: https://dashboard.stripe.com/acct_{accountId}/test/subscriptions/{subscriptionId}
    const productionPattern = /^https:\/\/dashboard\.stripe\.com\/acct_[A-Za-z0-9]+\/subscriptions\/(sub_[A-Za-z0-9]+)$/;
    const stagingPattern = /^https:\/\/dashboard\.stripe\.com\/acct_[A-Za-z0-9]+\/test\/subscriptions\/(sub_[A-Za-z0-9]+)$/;

    const productionMatch = link.match(productionPattern);
    const stagingMatch = link.match(stagingPattern);

    if (productionMatch) {
      return {
        valid: true,
        subscriptionId: productionMatch[1],
        accountId: link.match(/acct_([A-Za-z0-9]+)/)?.[1],
        environment: 'production'
      };
    } else if (stagingMatch) {
      return {
        valid: true,
        subscriptionId: stagingMatch[1],
        accountId: link.match(/acct_([A-Za-z0-9]+)/)?.[1],
        environment: 'staging'
      };
    } else {
      return {
        valid: false,
        error: 'Invalid Stripe subscription link format. Expected format: https://dashboard.stripe.com/acct_{accountId}/subscriptions/{subscriptionId} or https://dashboard.stripe.com/acct_{accountId}/test/subscriptions/{subscriptionId}'
      };
    }
  };

  const handleChangeSubscription = async () => {
    if (!stripeSubscriptionLink || !stripeSubscriptionLink.trim()) {
      setSubscriptionLinkError('Stripe subscription link is required');
      return;
    }

    const validation = validateStripeSubscriptionLink(stripeSubscriptionLink);
    if (!validation.valid) {
      setSubscriptionLinkError(validation.error);
      return;
    }

    if (!selectedUserEmailForSubscription) {
      setChangeSubscriptionError('Please select a user');
      return;
    }

    if (!workspaceInfo?.authAccountId || !workspaceInfo?.accountId || !workspaceInfo?.environment) {
      setChangeSubscriptionError('Workspace information is incomplete');
      return;
    }

    setChangingSubscription(true);
    setSubscriptionLinkError('');
    setChangeSubscriptionError('');
    setChangeSubscriptionSuccess('');

    try {
      const response = await api.post('/api/workspace/change-subscription', {
        authAccountId: workspaceInfo.authAccountId,
        accountId: workspaceInfo.accountId,
        accountSlug: workspaceInfo.accountSlug,
        environment: workspaceInfo.environment,
        userEmail: selectedUserEmailForSubscription,
        stripeSubscriptionId: validation.subscriptionId
      });

      setChangeSubscriptionSuccess(response.data.message || 'Subscription changed successfully');

      // Refresh workspace info after changing subscription
      setTimeout(async () => {
        try {
          let queryParams = `searchType=${encodeURIComponent(searchType)}`;

          if (searchType === 'slug') {
            queryParams += `&slug=${encodeURIComponent(searchValue)}`;
          } else if (searchType === 'stripeCustomerId') {
            queryParams += `&stripeCustomerId=${encodeURIComponent(searchValue)}`;
          } else if (searchType === 'stripeSubscriptionId') {
            queryParams += `&stripeSubscriptionId=${encodeURIComponent(searchValue)}`;
          }

          const refreshResponse = await api.get(`/api/workspace/info?${queryParams}`);
          setWorkspaceInfo(refreshResponse.data.data);
          setShowChangeSubscriptionModal(false);
          setStripeSubscriptionLink('');
          setChangeSubscriptionSuccess('');
        } catch (err) {
          console.error('Error refreshing workspace info:', err);
        }
      }, 2000);
    } catch (err) {
      console.error('Error changing subscription:', err);
      setChangeSubscriptionError(err.response?.data?.error || 'Failed to change subscription');
    } finally {
      setChangingSubscription(false);
    }
  };

  const openChangeSubscriptionModal = () => {
    setShowChangeSubscriptionModal(true);
    setStripeSubscriptionLink('');
    setSelectedUserEmailForSubscription('');
    setSubscriptionLinkError('');
    setChangeSubscriptionError('');
    setChangeSubscriptionSuccess('');
  };

  const handleAllowFreePlan = async () => {
    if (!workspaceInfo?.accountId || !workspaceInfo?.environment) {
      setAllowFreePlanError('Workspace information is incomplete');
      return;
    }

    if (workspaceInfo.allowFreePlan) {
      setAllowFreePlanSuccess('Free plan access is already enabled');
      return;
    }

    if (!window.confirm(`Enable free plan access for workspace "${workspaceInfo.accountSlug || workspaceInfo.accountId}"?`)) {
      return;
    }

    setAllowingFreePlan(true);
    setAllowFreePlanError('');
    setAllowFreePlanSuccess('');

    try {
      const response = await api.post('/api/workspace/allow-free-plan', {
        accountId: workspaceInfo.accountId,
        accountSlug: workspaceInfo.accountSlug,
        environment: workspaceInfo.environment,
      });

      setAllowFreePlanSuccess(response.data.message || 'Free plan access enabled successfully');
      setWorkspaceInfo((prev) => prev ? {
        ...prev,
        allowFreePlan: true,
        featureMapping: response.data.data?.featureMapping || {
          ...(prev.featureMapping || {}),
          added: Array.from(new Set([...(prev.featureMapping?.added || []), 'allowFreePlan'])),
        },
      } : prev);
    } catch (err) {
      console.error('Error enabling free plan access:', err);
      setAllowFreePlanError(err.response?.data?.error || 'Failed to enable free plan access');
    } finally {
      setAllowingFreePlan(false);
    }
  };

  const fetchAdoptionSnapshot = async () => {
    if (!workspaceInfo?.accountId) return;
    // Prevent duplicate calls if already loading
    if (adoptionLoading) {
      console.log('Adoption snapshot already loading, skipping duplicate call');
      return;
    }
    setAdoptionLoading(true);
    setAdoptionError('');
    try {
      const environment = workspaceInfo.environment || 'production';
      const res = await api.get(`/api/workspace/${workspaceInfo.accountId}/adoption?environment=${environment}`);
      setAdoptionSnapshot(res.data);
    } catch (err) {
      console.error('Error fetching adoption snapshot:', err);
      setAdoptionError(err.response?.data?.message || 'Failed to fetch adoption snapshot');
    } finally {
      setAdoptionLoading(false);
    }
  };

  // Helper function to extract Stripe Customer ID from URL
  const extractStripeCustomerId = (stripeCustomerId, stripeCustomerLink) => {
    if (!stripeCustomerId) return null;

    // If it's already just an ID (starts with cus_), return it trimmed
    if (stripeCustomerId.trim().startsWith('cus_')) {
      return stripeCustomerId.trim();
    }

    // If it's a URL, extract the customer ID from it
    if (stripeCustomerLink) {
      // Try to extract from URL patterns like:
      // https://dashboard.stripe.com/customers/cus_xxx
      // https://dashboard.stripe.com/test/customers/cus_xxx
      const match = stripeCustomerLink.match(/customers\/(cus_[a-zA-Z0-9]+)/);
      if (match && match[1]) {
        return match[1].trim();
      }
    }

    // Fallback: try to extract from stripeCustomerId itself if it contains a URL
    const urlMatch = stripeCustomerId.match(/customers\/(cus_[a-zA-Z0-9]+)/);
    if (urlMatch && urlMatch[1]) {
      return urlMatch[1].trim();
    }

    // If it looks like an ID, return trimmed
    if (stripeCustomerId.includes('cus_')) {
      const idMatch = stripeCustomerId.match(/(cus_[a-zA-Z0-9]+)/);
      if (idMatch && idMatch[1]) {
        return idMatch[1].trim();
      }
    }

    return stripeCustomerId.trim();
  };

  const fetchCompanyProfile = async (domain = null) => {
    setCompanyLoading(true);
    setCompanyError('');
    setCompanyProfile(null);
    try {
      const queryParams = new URLSearchParams({
        accountId: workspaceInfo.accountId
      });

      // Add company_domain if provided
      if (domain && domain.trim()) {
        queryParams.append('company_domain', domain);
      }

      // Add stripeCustomerId if available (extract from URL if needed)
      const stripeCustomerId = extractStripeCustomerId(
        workspaceInfo.stripeCustomerId,
        workspaceInfo.stipeCustomerLink
      );
      if (stripeCustomerId) {
        queryParams.append('stripeCustomerId', stripeCustomerId);
      }

      const response = await api.get(`/api/workspace/deep-research?${queryParams.toString()}`);
      const resultData = response.data?.data?.enrichment?.successfulRows?.[0] || null;
      if (resultData) {
        // Add source to the profile data for UI display
        resultData.source = response.data.source;
        setCompanyProfile(resultData);

        // Always update domain from API response to show normalized version
        if (response.data?.resolved_domain) {
          setResearchDomain(response.data.resolved_domain);
        }
        // Don't call fetchAdoptionSnapshot here - it's already called when workspaceInfo changes
      } else {
        setCompanyError('No data found for this company');
      }
    } catch (err) {
      console.error('Error fetching company profile:', err);
      setCompanyError(err.response?.data?.error || 'Failed to fetch company profile');
    } finally {
      setCompanyLoading(false);
    }
  };

  // Auto-call deep-research API when workspaceInfo loads (without domain)
  const deepResearchFetchedRef = useRef(false);
  useEffect(() => {
    // Reset ref when accountId changes
    if (workspaceInfo?.accountId) {
      const currentAccountId = workspaceInfo.accountId;
      if (deepResearchFetchedRef.current !== currentAccountId) {
        deepResearchFetchedRef.current = currentAccountId;
        // Call without domain to check DynamoDB or resolve domain
        if (!companyProfile && !companyLoading) {
          fetchCompanyProfile(null);
        }
      }
    }
  }, [workspaceInfo?.accountId]);

  const formatGroupOperator = (op) => {
    if (!op) return "ALL conditions";
    return op === "OR" ? "ANY condition (OR)" : "ALL conditions (AND)";
  };
  const createdIcps = platformUsage?.icps?.icps || [];
  const createdSignals = platformUsage?.signals?.signals || [];
  const goodIcps = createdIcps.filter(i => i.quality === 'good');
  const badIcps = createdIcps.filter(i => i.quality === 'bad');
  const goodSignals = createdSignals.filter(s => s.quality === 'good');
  const badSignals = createdSignals.filter(s => s.quality === 'bad');
  const cardStyle = {
    backgroundColor: 'white',
    border: '1px solid #e9ecef',
    borderRadius: '6px',
    padding: '16px'
  };
  const listStyle = {
    marginTop: '8px',
    paddingLeft: '18px',
    fontSize: '14px'
  };

  return (
    <div className="wi-page">
      <div className="wi-panel">
        <Card bordered={false} className="wi-search-card" styles={{ body: { padding: 24 } }}>
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <div>
              <Title level={3} style={{ margin: 0 }}>Workspace Information</Title>
              <Paragraph type="secondary" style={{ marginBottom: 0, marginTop: 4 }}>
                Search by workspace slug, Stripe Customer ID, or Stripe Subscription ID
              </Paragraph>
            </div>

            <Flex wrap="wrap" align="center" gap={12}>
              <Text type="secondary" strong>Environment</Text>
              <Segmented
                value={environment}
                onChange={setEnvironment}
                options={[
                  { label: 'Production', value: 'production' },
                  { label: 'Staging', value: 'staging' },
                ]}
              />
            </Flex>

            <div>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>Search</Text>
              <div className="wi-search-row">
                <Select
                  value={searchType}
                  onChange={handleSearchTypeChange}
                  style={{ minWidth: 200, width: '100%', maxWidth: 240 }}
                  options={[
                    { value: 'slug', label: 'Workspace Slug' },
                    { value: 'stripeCustomerId', label: 'Stripe Customer ID' },
                    { value: 'stripeSubscriptionId', label: 'Stripe Subscription ID' },
                  ]}
                />
                <div style={{ flex: 1, position: 'relative', minWidth: 0 }}>
                  <Input
                    ref={inputRef}
                    value={searchValue}
                    onChange={handleInputChange}
                    onPressEnter={handleKeyPress}
                    onFocus={() => {
                      if (searchType === 'slug' && suggestions.length > 0) {
                        setShowSuggestions(true);
                      }
                    }}
                    placeholder={getPlaceholder()}
                    allowClear
                    size="large"
                  />
                  {searchType === 'slug' && showSuggestions && suggestions.length > 0 && (
                    <Card
                      size="small"
                      ref={suggestionsRef}
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        zIndex: 1000,
                        marginTop: 4,
                        maxHeight: 300,
                        overflowY: 'auto',
                      }}
                      styles={{ body: { padding: 0 } }}
                    >
                      {loadingSuggestions ? (
                        <div style={{ padding: 12, textAlign: 'center' }}>
                          <Text type="secondary">Loading suggestions...</Text>
                        </div>
                      ) : (
                        suggestions.map((suggestion, index) => (
                          <div
                            key={`${suggestion.slug}-${index}`}
                            onClick={() => handleSuggestionClick(suggestion)}
                            className="wi-suggestion-item"
                            style={{
                              padding: '10px 14px',
                              cursor: 'pointer',
                              borderBottom: index < suggestions.length - 1 ? '1px solid var(--ant-color-split, #f0f0f0)' : 'none',
                            }}
                          >
                            <Text strong style={{ display: 'block' }}>{suggestion.slug}</Text>
                            <Text type="secondary" style={{ fontSize: 12 }}>{suggestion.environment}</Text>
                          </div>
                        ))
                      )}
                    </Card>
                  )}
                </div>
                <Button
                  type="primary"
                  size="large"
                  icon={<SearchOutlined />}
                  onClick={handleSearch}
                  loading={loading}
                >
                  Search
                </Button>
              </div>
            </div>

            {searchHistory.length > 0 && (
              <Flex wrap="wrap" gap={8} align="center">
                <Text type="secondary" style={{ fontSize: 12 }}>Recent searches</Text>
                {searchHistory.map((h, i) => (
                  <Tag
                    key={i}
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      setSearchType(h.type);
                      setSearchValue(h.value);
                      fetchWorkspaceInfo(h.type, h.value, h.environment || null);
                    }}
                  >
                    {h.value} ({h.type === 'slug' ? 'Slug' : h.type === 'stripeCustomerId' ? 'Stripe ID' : 'Sub ID'})
                  </Tag>
                ))}
                <Button
                  type="link"
                  size="small"
                  danger
                  onClick={() => {
                    setSearchHistory([]);
                    localStorage.removeItem('workspaceSearchHistory');
                  }}
                >
                  Clear
                </Button>
              </Flex>
            )}
          </Space>
        </Card>

        {error && (
          <Alert
            type="error"
            showIcon
            message={error}
            style={{ marginTop: 16, marginBottom: 8 }}
          />
        )}

        {workspaceInfo && (
          <Card
            title="Workspace Details"
            style={{ marginTop: 16 }}
            className="wi-details-card"
          >
            <Descriptions
              bordered
              size="middle"
              column={1}
              layout="horizontal"
              styles={{
                label: { width: 200, maxWidth: 220, fontWeight: 600 },
                content: { minWidth: 0 },
              }}
              className="wi-details-descriptions"
            >
              <Descriptions.Item label="Account Slug">
                <Text code>{workspaceInfo.accountSlug}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Account ID">
                <Text code copyable>{workspaceInfo.accountId}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Auth Account ID">
                <Text code copyable>{workspaceInfo.authAccountId}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Environment">
                <Tag color={workspaceInfo.environment === 'production' ? 'blue' : 'orange'}>
                  {workspaceInfo.environment}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Available Credits">
                <div className="wi-ops-actions wi-credits-sticky">
                  <div>
                    <Text
                      strong
                      style={{
                        fontSize: 20,
                        fontFamily: 'monospace',
                        color: (workspaceInfo.availableCredits || 0) > 0 ? undefined : undefined,
                      }}
                      type={(workspaceInfo.availableCredits || 0) > 0 ? 'success' : 'danger'}
                    >
                      {workspaceInfo.availableCredits !== undefined
                        ? workspaceInfo.availableCredits.toLocaleString()
                        : '0'}
                    </Text>
                    <Text type="secondary" style={{ marginLeft: 8 }}>credits</Text>
                    {workspaceInfo.creditsSnapshot && (
                      <div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {workspaceInfo.creditsSnapshot.inOverdraft
                            ? `Using overdraft: ${workspaceInfo.creditsSnapshot.overdraftUsed.toLocaleString()} of ${workspaceInfo.creditsSnapshot.overdraftLimit.toLocaleString()}.`
                            : `Overdraft limit: ${workspaceInfo.creditsSnapshot.overdraftLimit.toLocaleString()}. When the balance reaches 0, this overdraft will be used.`}
                        </Text>
                      </div>
                    )}
                  </div>
                  {hasPermission(PERMISSIONS.ADD_CREDITS_TO_WORKSPACE) && (
                    <Space wrap>
                      <Button type="primary" onClick={openAddCreditsModal}>
                        Add credits / Overdrafting
                      </Button>
                      <Button danger onClick={openDeductCreditsModal}>
                        Deduct Credits
                      </Button>
                    </Space>
                  )}
                </div>
              </Descriptions.Item>
              <Descriptions.Item label="Subscription Plan">
                <Tag color="processing" style={{ textTransform: 'uppercase' }}>
                  {workspaceInfo.subscriptionPlan}
                </Tag>
              </Descriptions.Item>
              {hasPermission(PERMISSIONS.CHANGE_SUBSCRIPTION) && (
                <Descriptions.Item label="Free Plan Access">
                  <Space wrap>
                    {workspaceInfo.allowFreePlan ? (
                      <Tag color="success">Enabled</Tag>
                    ) : (
                      <Button
                        type="default"
                        onClick={handleAllowFreePlan}
                        loading={allowingFreePlan}
                      >
                        Enable Free Plan
                      </Button>
                    )}
                    {!workspaceInfo.allowFreePlan && allowFreePlanSuccess && (
                      <Text type="success">{allowFreePlanSuccess}</Text>
                    )}
                    {allowFreePlanError && (
                      <Text type="danger">{allowFreePlanError}</Text>
                    )}
                  </Space>
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Subscription Status">
                <Tag color={getStatusColor(workspaceInfo.subscriptionStatus)}>
                  {workspaceInfo.subscriptionStatus}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Add-on subscriptions">
                {Array.isArray(workspaceInfo.subscriptionAddons) && workspaceInfo.subscriptionAddons.length > 0 ? (
                  <Table
                    size="small"
                    pagination={false}
                    rowKey={(row, idx) => row.stripeSubscriptionId || String(idx)}
                    scroll={{ x: true }}
                    dataSource={workspaceInfo.subscriptionAddons}
                    columns={[
                      {
                        title: 'Add-on',
                        dataIndex: 'addonName',
                        render: (name, row) => (
                          <div>
                            <Text code>{name}</Text>
                            {row.lookupKey && row.lookupKey !== row.addonName ? (
                              <div><Text type="secondary" style={{ fontSize: 11 }}>{row.lookupKey}</Text></div>
                            ) : null}
                          </div>
                        ),
                      },
                      { title: 'Qty', dataIndex: 'quantity', width: 64 },
                      {
                        title: 'Status',
                        dataIndex: 'subscriptionStatus',
                        render: (s) => <Tag color={getStatusColor(s)}>{s}</Tag>,
                      },
                      {
                        title: 'Current period end',
                        dataIndex: 'currentPeriodEnd',
                        render: (ts) => formatStripeUnixSeconds(ts),
                      },
                      {
                        title: 'Subscription ID',
                        dataIndex: 'stripeSubscriptionId',
                        render: (id) => id ? <Text code style={{ fontSize: 12 }}>{id}</Text> : '—',
                      },
                    ]}
                  />
                ) : (
                  <Text type="secondary" italic>
                    None recorded (synced from auth as subscription.addons)
                  </Text>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Stripe Customer Link">
                <div className="wi-ops-actions">
                  {workspaceInfo.stripeCustomerId && workspaceInfo.stipeCustomerLink ? (
                    <Button
                      type="link"
                      href={workspaceInfo.stipeCustomerLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      icon={<LinkOutlined />}
                      style={{ paddingInline: 0 }}
                    >
                      <Text code>{workspaceInfo.stripeCustomerId}</Text>
                    </Button>
                  ) : (
                    <Text type="secondary" italic>Not available</Text>
                  )}
                  {hasPermission(PERMISSIONS.CHANGE_SUBSCRIPTION) && (
                    <Button type="primary" ghost onClick={openChangeSubscriptionModal}>
                      Change Subscription
                    </Button>
                  )}
                </div>
              </Descriptions.Item>
            </Descriptions>

              {/* Sales Owner & Onboarding Owner */}
              <Card size="small" style={{ marginTop: 16 }} title="Owners">
                <Flex wrap="wrap" gap={24} justify="space-between" align="flex-start">
                  <Space direction="vertical" size={4} style={{ minWidth: 200 }}>
                    <Text type="secondary" strong style={{ fontSize: 12 }}>SALES OWNER</Text>
                    {isEditingOwners ? (
                      <Select
                        value={tempSalesOwner || undefined}
                        onChange={(v) => setTempSalesOwner(v || '')}
                        allowClear
                        placeholder="Unassigned"
                        style={{ width: '100%', minWidth: 200 }}
                        options={adminUsers.map((u) => ({ value: u.name, label: u.name }))}
                      />
                    ) : (
                      <Text type={workspaceInfo.salesOwner ? undefined : 'secondary'}>
                        {workspaceInfo.salesOwner || 'Unassigned'}
                      </Text>
                    )}
                  </Space>
                  <Space direction="vertical" size={4} style={{ minWidth: 200 }}>
                    <Text type="secondary" strong style={{ fontSize: 12 }}>ONBOARDING OWNER</Text>
                    {isEditingOwners ? (
                      <Select
                        value={tempOnboardingOwner || undefined}
                        onChange={(v) => setTempOnboardingOwner(v || '')}
                        allowClear
                        placeholder="Unassigned"
                        style={{ width: '100%', minWidth: 200 }}
                        options={adminUsers.map((u) => ({ value: u.name, label: u.name }))}
                      />
                    ) : (
                      <Text type={workspaceInfo.onboardingOwner ? undefined : 'secondary'}>
                        {workspaceInfo.onboardingOwner || 'Unassigned'}
                      </Text>
                    )}
                  </Space>
                  <div>
                    {isEditingOwners ? (
                      <Space>
                        <Button type="primary" onClick={handleSaveOwners} loading={isSavingOwners}>
                          Save
                        </Button>
                        <Button onClick={() => setIsEditingOwners(false)}>Cancel</Button>
                      </Space>
                    ) : (
                      <Button
                        onClick={() => {
                          setTempSalesOwner(workspaceInfo.salesOwner || '');
                          setTempOnboardingOwner(workspaceInfo.onboardingOwner || '');
                          setIsEditingOwners(true);
                        }}
                      >
                        Edit Owners
                      </Button>
                    )}
                  </div>
                </Flex>
              </Card>
          </Card>
        )}

        {/* Company Research Section */}
        {workspaceInfo && (
          <div style={{
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            padding: '25px',
            border: '1px solid #e9ecef',
            marginTop: '30px'
          }}>
            <h3 style={{ margin: 0, color: '#2c3e50', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '20px', fontWeight: 'bold', marginBottom: '20px', borderBottom: '2px solid #3498db', paddingBottom: '10px' }}>
              Company Research
            </h3>
            <div style={{ display: 'flex', gap: '12px' }}>
              <input
                type="text"
                placeholder="Enter company domain"
                value={researchDomain}
                onChange={(e) => setResearchDomain(e.target.value)}
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  border: '1px solid #ced4da',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    fetchCompanyProfile(researchDomain);
                  }
                }}
              />
              <button
                onClick={async (e) => {
                  // Save domain to DynamoDB when user manually changes and clicks fetch
                  fetchCompanyProfile(researchDomain);
                }}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#3498db',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Fetch
              </button>
            </div>
            {companyLoading && (
              <div style={{ marginTop: '20px', color: '#6c757d' }}>
                Loading company profile...
              </div>
            )}
            {companyError && (
              <div style={{
                marginTop: '20px',
                padding: '12px',
                backgroundColor: '#f8d7da',
                border: '1px solid #f5c6cb',
                borderRadius: '6px',
                color: '#721c24'
              }}>
                ⚠️ {companyError}
              </div>
            )}
            {companyProfile && (
              <div style={{
                marginTop: '25px',
                backgroundColor: 'white',
                borderRadius: '8px',
                border: '1px solid #e9ecef',
                padding: '20px'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: 'bold',
                    color: '#2c3e50',
                    margin: 0
                  }}>
                    {companyProfile.company_name || companyProfile.name}
                  </h3>
                  {companyProfile.source && (
                    <span style={{
                      fontSize: '12px',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      backgroundColor: companyProfile.source === 'CACHE' ? '#d4edda' : '#cce5ff',
                      color: companyProfile.source === 'CACHE' ? '#155724' : '#004085',
                      fontWeight: '600'
                    }}>
                      {companyProfile.source === 'CACHE' ? 'Cached' : 'AI Generated'}
                    </span>
                  )}
                </div>

                {companyProfile.description && (
                  <div style={{ marginBottom: '20px', color: '#555', fontSize: '14px', lineHeight: '1.5' }}>
                    {companyProfile.description}
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  {/* Left Column: Core Info */}
                  <div style={{ display: 'grid', gap: '10px', alignContent: 'start' }}>
                    <div><strong>Industry:</strong> {companyProfile.industry || 'Not available'}</div>
                    {companyProfile.core_services && (
                      <div>
                        <strong>Core Services:</strong>{' '}
                        {Array.isArray(companyProfile.core_services)
                          ? companyProfile.core_services.join(', ')
                          : companyProfile.core_services
                        }
                      </div>
                    )}
                    <div><strong>Employee Size:</strong> {companyProfile.employee_size_range || companyProfile.employee_size || 'Not available'}</div>
                    <div><strong>Sales Team Size:</strong> {companyProfile.sales_team_size_range || 'Not available'}</div>
                    <div><strong>Founded Year:</strong> {companyProfile.founded_year || 'Not available'}</div>
                    <div><strong>HQ Country:</strong> {companyProfile.hq_country || companyProfile.hq?.country || 'Not available'}</div>
                    <div><strong>Customer Type:</strong>{' '}
                      {companyProfile.customer_type || (
                        <span style={{ color: '#6c757d', fontStyle: 'italic' }}>
                          Not available</span>
                      )}</div>
                    <div style={{ display: 'grid', gap: '10px', marginTop: '10px' }}>
                      <div>
                        <strong>Who They Sell To:</strong>{' '}
                        {companyProfile.who_they_sell_to || (
                          <span style={{ color: '#6c757d', fontStyle: 'italic' }}>
                            Not available
                          </span>
                        )}
                      </div>
                      <div>
                        <strong>How They Make Money:</strong>{' '}
                        {companyProfile.how_they_make_money || (
                          <span style={{ color: '#6c757d', fontStyle: 'italic' }}>
                            Not available
                          </span>
                        )}
                      </div>
                      <div>
                        <strong>Why Highperformr Fits:</strong>{' '}
                        {companyProfile.why_highperformr_fits || (
                          <span style={{ color: '#6c757d', fontStyle: 'italic' }}>
                            Not available
                          </span>
                        )}
                      </div>
                    </div>
                    <div style={{ marginTop: '10px' }}>
                      <strong>Website:</strong>{' '}
                      {companyProfile.website ? (
                        <a href={`https://${companyProfile.website}`} target="_blank" rel="noopener noreferrer" style={{ color: '#3498db' }}>
                          {companyProfile.website}
                        </a>
                      ) : 'Not available'}
                    </div>
                    <div>
                      <strong>LinkedIn:</strong>{' '}
                      {companyProfile.linkedin_url ? (
                        <a href={companyProfile.linkedin_url} target="_blank" rel="noopener noreferrer" style={{ color: '#0077b5' }}>
                          View Profile
                        </a>
                      ) : 'Not available'}
                    </div>
                  </div>

                  {/* Right Column: Financials & Funding */}
                  <div style={{ display: 'grid', gap: '10px', alignContent: 'start' }}>
                    <div><strong>Last Funding Round Name:</strong> {companyProfile.last_funding_round_name || 'Not available'}</div>
                    <div><strong>Last Funding Date:</strong> {companyProfile.last_funding_round_announced_date || companyProfile.last_funding_round_date || 'Not available'}</div>
                    <div>
                      <strong>Investors:</strong>
                      <div style={{ marginTop: '4px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {Array.isArray(companyProfile.investors) ? (
                          companyProfile.investors.map((inv, i) => (
                            <span key={i} style={{ backgroundColor: '#f1f3f5', padding: '2px 8px', borderRadius: '12px', fontSize: '12px' }}>{inv}</span>
                          ))
                        ) : Array.isArray(companyProfile.last_funding_round_lead_investors) ? (
                          companyProfile.last_funding_round_lead_investors.map((inv, i) => (
                            <span key={i} style={{ backgroundColor: '#f1f3f5', padding: '2px 8px', borderRadius: '12px', fontSize: '12px' }}>{inv}</span>
                          ))
                        ) : (companyProfile.last_funding_round_lead_investors || companyProfile.investors || 'Not available')}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Competitors */}
                <div style={{ marginTop: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <strong>Competitors:</strong>
                    {!addingType && (
                      <button
                        onClick={() => handleAddClick('competitors')}
                        style={{ fontSize: '11px', padding: '2px 6px', cursor: 'pointer', border: '1px solid #ced4da', borderRadius: '4px', background: '#fff' }}
                      >
                        + Add Competitor
                      </button>
                    )}
                  </div>
                  {addingType === 'competitors' && (
                    <div style={{ marginTop: '10px', padding: '10px', border: '1px solid #ced4da', borderRadius: '4px', backgroundColor: '#f8f9fa' }}>
                      <div style={{ marginBottom: '8px' }}>
                        <input
                          type="text"
                          placeholder="Name"
                          value={newItemData.name || ''}
                          onChange={(e) => setNewItemData({ ...newItemData, name: e.target.value })}
                          style={{ padding: '4px', fontSize: '12px', borderRadius: '4px', border: '1px solid #ced4da', marginRight: '8px' }}
                        />
                        <input
                          type="text"
                          placeholder="Domain"
                          value={newItemData.domain || ''}
                          onChange={(e) => setNewItemData({ ...newItemData, domain: e.target.value })}
                          style={{ padding: '4px', fontSize: '12px', borderRadius: '4px', border: '1px solid #ced4da' }}
                        />
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button onClick={handleSaveAdd} disabled={isSavingResearch} style={{ fontSize: '12px', padding: '4px 8px', borderRadius: '4px', border: 'none', backgroundColor: '#28a745', color: '#fff', cursor: 'pointer' }}>
                          {isSavingResearch ? 'Saving...' : 'Save'}
                        </button>
                        <button onClick={handleCancelAdd} disabled={isSavingResearch} style={{ fontSize: '12px', padding: '4px 8px', borderRadius: '4px', border: '1px solid #ced4da', backgroundColor: '#fff', cursor: 'pointer' }}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                  <div style={{ marginTop: '6px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {Array.isArray(companyProfile.competitors) ? (
                      companyProfile.competitors.map((comp, i) => {
                        const name = typeof comp === 'object' ? comp.name : comp;
                        const reason = typeof comp === 'object' ? comp.reason : '';
                        const domain = typeof comp === 'object' ? comp.domain : '';

                        const content = (
                          <span key={i} title={reason} style={{ backgroundColor: '#fff3cd', color: '#856404', padding: '4px 10px', borderRadius: '12px', fontSize: '13px', border: '1px solid #ffeeba', cursor: reason ? 'help' : 'default', display: 'inline-block' }}>
                            {name} {domain ? `(${domain})` : ''}
                          </span>
                        );
                        if (domain) {
                          return (
                            <a key={i} href={`https://${domain}`} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                              {content}
                            </a>
                          );
                        }
                        return content;
                      })
                    ) : (companyProfile.competitors || 'Not available')}
                  </div>
                </div>
                {/* Suggested ICPs */}
                <div style={{ marginTop: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <strong>Suggested ICPs:</strong>
                    {!addingType && (
                      <button
                        onClick={() => handleAddClick('icps')}
                        style={{ fontSize: '11px', padding: '2px 6px', cursor: 'pointer', border: '1px solid #ced4da', borderRadius: '4px', background: '#fff' }}
                      >
                        + Add ICP
                      </button>
                    )}
                  </div>
                  {addingType === 'icps' && (
                    <div style={{ marginTop: '10px', padding: '10px', border: '1px solid #ced4da', borderRadius: '4px', backgroundColor: '#f8f9fa' }}>
                      <div style={{ marginBottom: '8px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <input
                          type="text"
                          placeholder="Name"
                          value={newItemData.name || ''}
                          onChange={(e) => setNewItemData({ ...newItemData, name: e.target.value })}
                          style={{ padding: '4px', fontSize: '12px', borderRadius: '4px', border: '1px solid #ced4da' }}
                        />
                        <textarea
                          placeholder="Description (Optional)"
                          value={newItemData.description || ''}
                          onChange={(e) => setNewItemData({ ...newItemData, description: e.target.value })}
                          style={{ padding: '4px', fontSize: '12px', borderRadius: '4px', border: '1px solid #ced4da', minHeight: '60px' }}
                        />
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '12px', fontWeight: 'bold' }}>Match Logic:</span>
                          <select
                            value={newItemData.condition_match_logic || 'ALL'}
                            onChange={(e) => setNewItemData({ ...newItemData, condition_match_logic: e.target.value })}
                            style={{ padding: '4px', fontSize: '12px', borderRadius: '4px', border: '1px solid #ced4da' }}
                          >
                            <option value="ALL">ALL</option>
                            <option value="ANY">ANY</option>
                          </select>
                        </div>
                        <div style={{ marginTop: '4px' }}>
                          <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '4px' }}>Filters:</div>
                          {(newItemData.filters || []).map((filter, fIdx) => (
                            <div key={fIdx} style={{ display: 'flex', gap: '4px', marginBottom: '4px', alignItems: 'center' }}>
                              <input
                                placeholder="Field (e.g. contact.title)"
                                value={filter.field || ''}
                                onChange={(e) => {
                                  const newFilters = [...(newItemData.filters || [])];
                                  newFilters[fIdx] = { ...newFilters[fIdx], field: e.target.value };
                                  setNewItemData({ ...newItemData, filters: newFilters });
                                }}
                                style={{ flex: 1, padding: '4px', fontSize: '11px', borderRadius: '4px', border: '1px solid #ced4da' }}
                              />
                              <input
                                placeholder="Op"
                                value={filter.operator || ''}
                                onChange={(e) => {
                                  const newFilters = [...(newItemData.filters || [])];
                                  newFilters[fIdx] = { ...newFilters[fIdx], operator: e.target.value };
                                  setNewItemData({ ...newItemData, filters: newFilters });
                                }}
                                style={{ width: '60px', padding: '4px', fontSize: '11px', borderRadius: '4px', border: '1px solid #ced4da' }}
                              />
                              <input
                                placeholder="Values (comma separated)"
                                value={Array.isArray(filter.values) ? filter.values.join(', ') : (filter.values || '')}
                                onChange={(e) => {
                                  const newFilters = [...(newItemData.filters || [])];
                                  newFilters[fIdx] = { ...newFilters[fIdx], values: e.target.value.split(',').map(v => v.trim()) };
                                  setNewItemData({ ...newItemData, filters: newFilters });
                                }}
                                style={{ flex: 1, padding: '4px', fontSize: '11px', borderRadius: '4px', border: '1px solid #ced4da' }}
                              />
                              <button
                                onClick={() => {
                                  const newFilters = (newItemData.filters || []).filter((_, i) => i !== fIdx);
                                  setNewItemData({ ...newItemData, filters: newFilters });
                                }}
                                style={{ padding: '2px 6px', cursor: 'pointer', border: 'none', background: 'none', color: '#dc3545' }}
                              >
                                ✕
                              </button>
                            </div>
                          ))}
                          <button
                            onClick={() => {
                              const newFilters = [...(newItemData.filters || []), { field: '', operator: 'IN LIKE', values: [] }];
                              setNewItemData({ ...newItemData, filters: newFilters });
                            }}
                            style={{ fontSize: '11px', padding: '2px 6px', cursor: 'pointer', border: '1px solid #ced4da', borderRadius: '4px', background: '#fff', marginTop: '4px' }}
                          >
                            + Add Filter
                          </button>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button onClick={handleSaveAdd} disabled={isSavingResearch} style={{ fontSize: '12px', padding: '4px 8px', borderRadius: '4px', border: 'none', backgroundColor: '#28a745', color: '#fff', cursor: 'pointer' }}>
                          {isSavingResearch ? 'Saving...' : 'Save'}
                        </button>
                        <button onClick={handleCancelAdd} disabled={isSavingResearch} style={{ fontSize: '12px', padding: '4px 8px', borderRadius: '4px', border: '1px solid #ced4da', backgroundColor: '#fff', cursor: 'pointer' }}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                  <div style={{ marginTop: '6px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {(() => {
                      const rawIcps = companyProfile.suggested_icps || [];
                      const personas = companyProfile.suggested_icp_personas || companyProfile.suggested_target_icp_personas || [];

                      let icps = [];
                      if (rawIcps.length > 0) {
                        icps = rawIcps.map((ticp, idx) => {
                          const persona = personas[idx] || {};
                          return {
                            ...ticp,
                            persona_name: persona.persona_name || ticp.name,
                            reason: persona.reason || ticp.description
                          };
                        });
                      } else {
                        icps = personas;
                      }
                      return Array.isArray(icps) ? (
                        icps.map((icp, i) => {
                          const name = typeof icp === 'object' ? (icp.persona_name || icp.name || icp.persona) : icp;
                          const reason = typeof icp === 'object' ? (icp.reason || icp.description || '') : '';
                          const filters = typeof icp === 'object' ? (icp.filters || icp.filter || icp.conditions || []) : [];
                          const logic = typeof icp === 'object' ? (icp.condition_match_logic || icp.match_logic || icp.logic || '') : '';
                          const isCreated = typeof icp === 'object' ? icp.is_created : false;

                          return (
                            <div key={i} style={{ display: 'inline-block' }}>
                              <button
                                onClick={() => toggleIcpCondition(i)}
                                title={reason}
                                style={{
                                  backgroundColor: isCreated ? '#d4edda' : '#d1ecf1',
                                  color: isCreated ? '#155724' : '#0c5460',
                                  padding: '6px 12px',
                                  borderRadius: '12px',
                                  fontSize: '13px',
                                  border: isCreated ? '1px solid #c3e6cb' : '1px solid #bee5eb',
                                  cursor: 'pointer',
                                  marginRight: '8px',
                                  marginBottom: '6px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px'
                                }}
                              >
                                {isCreated && <span style={{ fontSize: '14px' }}>✓</span>}
                                {name}
                              </button>
                              {expanded.icpConditions?.[i] && (
                                <div style={{ marginTop: '6px', marginBottom: '8px', padding: '10px', border: '1px solid #e9ecef', borderRadius: '6px', background: '#ffffff' }}>
                                  <div
                                    style={{
                                      padding: '6px 10px',
                                      backgroundColor: isCreated ? '#d4edda' : '#f8d7da',
                                      border: `1px solid ${isCreated ? '#c3e6cb' : '#f5c6cb'}`,
                                      borderRadius: '4px',
                                      fontSize: '12px',
                                      color: isCreated ? '#155724' : '#721c24',
                                      marginBottom: '8px',
                                      fontWeight: '600'
                                    }}
                                  >
                                    {isCreated
                                      ? '✓ Already created in workspace'
                                      : '✕ Not created in workspace'}
                                  </div>
                                  <div style={{ fontSize: '13px', marginBottom: '6px' }}><strong>Match Logic:</strong> {logic || '—'}</div>
                                  <FiltersList filters={filters} />
                                </div>
                              )}
                            </div>
                          );
                        })
                      ) : (icps || 'Not available');
                    })()}
                  </div>
                </div>
                {/* Suggested Signals */}
                <div style={{ marginTop: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <strong>Suggested Signals:</strong>
                    {!addingType && (
                      <button
                        onClick={() => handleAddClick('signals')}
                        style={{ fontSize: '11px', padding: '2px 6px', cursor: 'pointer', border: '1px solid #ced4da', borderRadius: '4px', background: '#fff' }}
                      >
                        + Add Signal
                      </button>
                    )}
                  </div>
                  {addingType === 'signals' && (
                    <div style={{ marginTop: '10px', padding: '10px', border: '1px solid #ced4da', borderRadius: '4px', backgroundColor: '#f8f9fa' }}>
                      <div style={{ marginBottom: '8px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <input
                          type="text"
                          placeholder="Name"
                          value={newItemData.name || ''}
                          onChange={(e) => setNewItemData({ ...newItemData, name: e.target.value })}
                          style={{ padding: '4px', fontSize: '12px', borderRadius: '4px', border: '1px solid #ced4da' }}
                        />
                        <textarea
                          placeholder="Description (Optional)"
                          value={newItemData.description || ''}
                          onChange={(e) => setNewItemData({ ...newItemData, description: e.target.value })}
                          style={{ padding: '4px', fontSize: '12px', borderRadius: '4px', border: '1px solid #ced4da', minHeight: '60px' }}
                        />
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '12px', fontWeight: 'bold' }}>Match Logic:</span>
                          <select
                            value={newItemData.condition_match_logic || 'ANY'}
                            onChange={(e) => setNewItemData({ ...newItemData, condition_match_logic: e.target.value })}
                            style={{ padding: '4px', fontSize: '12px', borderRadius: '4px', border: '1px solid #ced4da' }}
                          >
                            <option value="ALL">ALL</option>
                            <option value="ANY">ANY</option>
                          </select>
                        </div>
                        <div style={{ marginTop: '4px' }}>
                          <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '4px' }}>Filters:</div>
                          {(newItemData.filters || []).map((filter, fIdx) => (
                            <div key={fIdx} style={{ display: 'flex', gap: '4px', marginBottom: '4px', alignItems: 'center' }}>
                              <input
                                placeholder="Field (e.g. contact.title)"
                                value={filter.field || ''}
                                onChange={(e) => {
                                  const newFilters = [...(newItemData.filters || [])];
                                  newFilters[fIdx] = { ...newFilters[fIdx], field: e.target.value };
                                  setNewItemData({ ...newItemData, filters: newFilters });
                                }}
                                style={{ flex: 1, padding: '4px', fontSize: '11px', borderRadius: '4px', border: '1px solid #ced4da' }}
                              />
                              <input
                                placeholder="Op"
                                value={filter.operator || ''}
                                onChange={(e) => {
                                  const newFilters = [...(newItemData.filters || [])];
                                  newFilters[fIdx] = { ...newFilters[fIdx], operator: e.target.value };
                                  setNewItemData({ ...newItemData, filters: newFilters });
                                }}
                                style={{ width: '60px', padding: '4px', fontSize: '11px', borderRadius: '4px', border: '1px solid #ced4da' }}
                              />
                              <input
                                placeholder="Values (comma separated)"
                                value={Array.isArray(filter.values) ? filter.values.join(', ') : (filter.values || '')}
                                onChange={(e) => {
                                  const newFilters = [...(newItemData.filters || [])];
                                  newFilters[fIdx] = { ...newFilters[fIdx], values: e.target.value.split(',').map(v => v.trim()) };
                                  setNewItemData({ ...newItemData, filters: newFilters });
                                }}
                                style={{ flex: 1, padding: '4px', fontSize: '11px', borderRadius: '4px', border: '1px solid #ced4da' }}
                              />
                              <button
                                onClick={() => {
                                  const newFilters = (newItemData.filters || []).filter((_, i) => i !== fIdx);
                                  setNewItemData({ ...newItemData, filters: newFilters });
                                }}
                                style={{ padding: '2px 6px', cursor: 'pointer', border: 'none', background: 'none', color: '#dc3545' }}
                              >
                                ✕
                              </button>
                            </div>
                          ))}
                          <button
                            onClick={() => {
                              const newFilters = [...(newItemData.filters || []), { field: '', operator: 'IN LIKE', values: [] }];
                              setNewItemData({ ...newItemData, filters: newFilters });
                            }}
                            style={{ fontSize: '11px', padding: '2px 6px', cursor: 'pointer', border: '1px solid #ced4da', borderRadius: '4px', background: '#fff', marginTop: '4px' }}
                          >
                            + Add Filter
                          </button>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button onClick={handleSaveAdd} disabled={isSavingResearch} style={{ fontSize: '12px', padding: '4px 8px', borderRadius: '4px', border: 'none', backgroundColor: '#28a745', color: '#fff', cursor: 'pointer' }}>
                          {isSavingResearch ? 'Saving...' : 'Save'}
                        </button>
                        <button onClick={handleCancelAdd} disabled={isSavingResearch} style={{ fontSize: '12px', padding: '4px 8px', borderRadius: '4px', border: '1px solid #ced4da', backgroundColor: '#fff', cursor: 'pointer' }}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                  <div style={{ marginTop: '6px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {(() => {
                      const signals = companyProfile.suggested_signals || companyProfile.suggestedSignals || companyProfile.suggested_signals;
                      if (!Array.isArray(signals) || signals.length === 0) return 'Not available';
                      return signals.map((s, idx) => {
                        const name = typeof s === 'object' ? (s.name || s.signal_name || `Signal ${idx + 1}`) : s;
                        const desc = typeof s === 'object' ? (s.description || s.reason || '') : '';
                        const filters = typeof s === 'object' ? (s.filters || s.filter || s.conditions || []) : [];
                        const logic = typeof s === 'object' ? (s.condition_match_logic || s.match_logic || s.logic || '') : '';
                        const isCreated = typeof s === 'object' ? s.is_created : false;

                        return (
                          <div key={idx} style={{ display: 'inline-block' }}>
                            <button
                              onClick={() => toggleSignalCondition(idx)}
                              title={desc}
                              style={{
                                backgroundColor: isCreated ? '#d4edda' : '#fff3cd',
                                color: isCreated ? '#155724' : '#856404',
                                padding: '6px 12px',
                                borderRadius: '12px',
                                fontSize: '13px',
                                border: isCreated ? '1px solid #c3e6cb' : '1px solid #ffeeba',
                                cursor: 'pointer',
                                marginRight: '8px',
                                marginBottom: '6px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px'
                              }}
                            >
                              {isCreated && <span style={{ fontSize: '14px' }}>✓</span>}
                              {name}
                            </button>
                            {expanded.signalConditions?.[idx] && (
                              <div style={{ marginTop: '6px', marginBottom: '8px', padding: '10px', border: '1px solid #e9ecef', borderRadius: '6px', background: '#ffffff' }}>
                                <div
                                  style={{
                                    padding: '6px 10px',
                                    backgroundColor: isCreated ? '#d4edda' : '#f8d7da',
                                    border: `1px solid ${isCreated ? '#c3e6cb' : '#f5c6cb'}`,
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                    color: isCreated ? '#155724' : '#721c24',
                                    marginBottom: '8px',
                                    fontWeight: '600'
                                  }}
                                >
                                  {isCreated
                                    ? '✓ Already created in workspace'
                                    : '✕ Not created in workspace'}
                                </div>
                                <div style={{ fontSize: '13px', marginBottom: '6px' }}><strong>Match Logic:</strong> {logic || '—'}</div>
                                <FiltersList filters={filters} />
                              </div>
                            )}
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>
                {/* Funding Rounds Table */}
                {Array.isArray(companyProfile.funding_rounds) && companyProfile.funding_rounds.length > 0 && (
                  <div style={{ marginTop: '20px' }}>
                    <strong>Funding Rounds:</strong>
                    <table style={{ width: '100%', marginTop: '8px', borderCollapse: 'collapse', fontSize: '13px' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f8f9fa', textAlign: 'left' }}>
                          <th style={{ padding: '8px', borderBottom: '2px solid #dee2e6' }}>Round</th>
                          <th style={{ padding: '8px', borderBottom: '2px solid #dee2e6' }}>Date</th>
                          <th style={{ padding: '8px', borderBottom: '2px solid #dee2e6' }}>Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {companyProfile.funding_rounds.map((round, i) => (
                          <tr key={i} style={{ borderBottom: '1px solid #dee2e6' }}>
                            <td style={{ padding: '8px' }}>{round.round}</td>
                            <td style={{ padding: '8px' }}>{round.date}</td>
                            <td style={{ padding: '8px' }}>{round.amount}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Generate Upgrade Link Section */}
        {workspaceInfo && (
          <div style={{
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            padding: '25px',
            border: '1px solid #e9ecef',
            marginTop: '30px'
          }}>
            <h2 style={{
              fontSize: '20px',
              fontWeight: 'bold',
              marginBottom: '20px',
              color: '#2c3e50',
              borderBottom: '2px solid #3498db',
              paddingBottom: '10px'
            }}>
              🔗 Generate Upgrade Link
            </h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '600',
                  color: '#495057',
                  fontSize: '14px'
                }}>
                  Select Plan
                </label>
                <select
                  value={selectedPlan}
                  onChange={(e) => {
                    setSelectedPlan(e.target.value);
                    setUpgradeLink('');
                    setUpgradeLinkError('');
                  }}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid #ced4da',
                    borderRadius: '6px',
                    fontSize: '14px',
                    outline: 'none',
                    backgroundColor: 'white',
                    cursor: 'pointer',
                    fontWeight: '500',
                    boxSizing: 'border-box'
                  }}
                >
                  <option value="">-- Select a plan --</option>
                  {availablePlans.map(plan => (
                    <option key={plan.value} value={plan.value}>
                      {plan.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '600',
                  color: '#495057',
                  fontSize: '14px'
                }}>
                  Generate on request of
                </label>
                <select
                  value={selectedUserEmail}
                  onChange={(e) => {
                    setSelectedUserEmail(e.target.value);
                    setUpgradeLink('');
                    setUpgradeLinkError('');
                  }}
                  disabled={loadingUsers || workspaceUsers.length === 0}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid #ced4da',
                    borderRadius: '6px',
                    fontSize: '14px',
                    outline: 'none',
                    backgroundColor: loadingUsers || workspaceUsers.length === 0 ? '#f8f9fa' : 'white',
                    cursor: loadingUsers || workspaceUsers.length === 0 ? 'not-allowed' : 'pointer',
                    fontWeight: '500',
                    boxSizing: 'border-box',
                    color: loadingUsers || workspaceUsers.length === 0 ? '#6c757d' : '#212529'
                  }}
                >
                  <option value="">
                    {loadingUsers ? 'Loading users...' : workspaceUsers.length === 0 ? 'No users available' : '-- Select a user --'}
                  </option>
                  {workspaceUsers.map(user => (
                    <option key={user.email} value={user.email}>
                      {user.name} ({user.email})
                    </option>
                  ))}
                </select>
              </div>

              <button
                onClick={handleGenerateUpgradeLink}
                disabled={generatingLink || !selectedPlan || !selectedUserEmail}
                style={{
                  padding: '12px 24px',
                  backgroundColor: generatingLink || !selectedPlan || !selectedUserEmail ? '#95a5a6' : '#3498db',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: generatingLink || !selectedPlan || !selectedUserEmail ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  transition: 'background-color 0.2s ease',
                  alignSelf: 'flex-start'
                }}
                onMouseEnter={(e) => {
                  if (!generatingLink && selectedPlan && selectedUserEmail) e.target.style.backgroundColor = '#2980b9';
                }}
                onMouseLeave={(e) => {
                  if (!generatingLink && selectedPlan && selectedUserEmail) e.target.style.backgroundColor = '#3498db';
                }}
              >
                {generatingLink ? 'Generating...' : '🔗 Generate Upgrade Link'}
              </button>

              {upgradeLinkError && (
                <div style={{
                  padding: '12px 16px',
                  backgroundColor: '#f8d7da',
                  border: '1px solid #f5c6cb',
                  borderRadius: '6px',
                  color: '#721c24',
                  fontSize: '14px'
                }}>
                  ⚠️ {upgradeLinkError}
                </div>
              )}

              {upgradeLink && (
                <div style={{
                  padding: '15px',
                  backgroundColor: 'white',
                  borderRadius: '6px',
                  border: '1px solid #c3e6cb'
                }}>
                  <div style={{
                    marginBottom: '10px',
                    padding: '8px',
                    backgroundColor: '#d4edda',
                    border: '1px solid #c3e6cb',
                    borderRadius: '4px',
                    fontSize: '12px',
                    color: '#155724',
                    fontWeight: '600'
                  }}>
                    ✅ Upgrade link generated successfully
                  </div>
                  <div style={{
                    padding: '10px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontFamily: 'monospace',
                    wordBreak: 'break-all',
                    color: '#495057',
                    border: '1px solid #e9ecef',
                    marginBottom: '10px'
                  }}>
                    {upgradeLink}
                  </div>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      id="copy-upgrade-link-btn"
                      onClick={copyUpgradeLinkToClipboard}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#2c3e50',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: '600',
                        transition: 'background-color 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        if (e.target.textContent !== '✓ Copied!') e.target.style.backgroundColor = '#34495e';
                      }}
                      onMouseLeave={(e) => {
                        if (e.target.textContent !== '✓ Copied!') e.target.style.backgroundColor = '#2c3e50';
                      }}
                    >
                      📋 Copy Link
                    </button>
                    <a
                      href={upgradeLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#28a745',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: '600',
                        textDecoration: 'none',
                        display: 'inline-block',
                        transition: 'background-color 0.2s ease'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#218838'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = '#28a745'}
                    >
                      🔗 Open Link
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Workspace Users Section */}
        {workspaceInfo && (
          <div style={{
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            padding: '25px',
            border: '1px solid #e9ecef',
            marginTop: '30px'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
              borderBottom: '2px solid #3498db',
              paddingBottom: '10px'
            }}>
              <h2 style={{
                fontSize: '20px',
                fontWeight: 'bold',
                margin: 0,
                color: '#2c3e50',
              }}>
                👥 Workspace Users {workspaceUsers.length > 0 && (
                  <span style={{ fontSize: '16px', fontWeight: 'normal', color: '#6c757d', marginLeft: '10px' }}>
                    ({workspaceUsers.filter(u => {
                      const activeUserEmails = new Set((platformUsage?.users?.users || []).filter(pu => pu.isActive).map(pu => pu.email));
                      return activeUserEmails.has(u.email);
                    }).length}/{workspaceUsers.length} Active)
                  </span>
                )}
              </h2>
              {workspaceUsers.length > 0 && (
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '14px', color: '#495057' }}>
                  <input
                    type="checkbox"
                    checked={showActiveUsersOnly}
                    onChange={(e) => setShowActiveUsersOnly(e.target.checked)}
                    style={{ marginRight: '8px' }}
                  />
                  Show active users for the last 14 days
                </label>
              )}
            </div>

            {loadingUsers ? (
              <div style={{
                padding: '20px',
                textAlign: 'center',
                color: '#6c757d'
              }}>
                Loading users...
              </div>
            ) : workspaceUsers.length === 0 ? (
              <div style={{
                padding: '20px',
                textAlign: 'center',
                color: '#6c757d'
              }}>
                No users found for this workspace
              </div>
            ) : (
              <div
                className="wi-users-table-wrap"
                style={{
                  backgroundColor: theme.cardBackground,
                  borderRadius: '6px',
                  border: `1px solid ${theme.border}`,
                  maxHeight: '400px',
                  overflowY: 'auto'
                }}
              >
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  minWidth: '480px'
                }}>
                  <thead>
                    <tr style={{
                      backgroundColor: '#f8f9fa',
                      borderBottom: '2px solid #dee2e6'
                    }}>
                      <th style={{
                        padding: '12px 16px',
                        textAlign: 'left',
                        fontWeight: '600',
                        color: '#495057',
                        fontSize: '14px',
                        borderBottom: '2px solid #dee2e6'
                      }}>
                        Name
                      </th>
                      <th style={{
                        padding: '12px 16px',
                        textAlign: 'left',
                        fontWeight: '600',
                        color: '#495057',
                        fontSize: '14px',
                        borderBottom: '2px solid #dee2e6'
                      }}>
                        Email
                      </th>
                      {hasPermission(PERMISSIONS.USER_SSO) && (
                        <th style={{
                          padding: '12px 16px',
                          textAlign: 'left',
                          fontWeight: '600',
                          color: '#495057',
                          fontSize: '14px',
                          borderBottom: '2px solid #dee2e6'
                        }}>
                          SSO Login
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {(showActiveUsersOnly ? workspaceUsers.filter(u => {
                      const activeUserEmails = new Set((platformUsage?.users?.users || []).filter(pu => pu.isActive).map(pu => pu.email));
                      return activeUserEmails.has(u.email);
                    }) : workspaceUsers).map((workspaceUser, index) => {
                      const userState = userSSOStates[workspaceUser.email] || {};
                      return (
                        <tr
                          key={workspaceUser.authUserId || index}
                          style={{
                            borderBottom: index < workspaceUsers.length - 1 ? '1px solid #e9ecef' : 'none',
                            transition: 'background-color 0.2s ease'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                        >
                          <td style={{
                            padding: '12px 16px',
                            color: '#212529',
                            fontSize: '14px'
                          }}>
                            {workspaceUser.name}
                          </td>
                          <td style={{
                            padding: '12px 16px',
                            color: '#212529',
                            fontSize: '14px',
                            fontFamily: 'monospace'
                          }}>
                            {workspaceUser.email}
                          </td>
                          {hasPermission(PERMISSIONS.USER_SSO) && (
                            <td style={{
                              padding: '12px 16px'
                            }}>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {!userState.loginUrl ? (
                                  <button
                                    onClick={() => generateUserSSO(workspaceUser.email, workspaceInfo.environment)}
                                    disabled={userState.loading}
                                    style={{
                                      padding: '6px 12px',
                                      backgroundColor: userState.loading ? '#95a5a6' : '#3498db',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '4px',
                                      cursor: userState.loading ? 'not-allowed' : 'pointer',
                                      fontSize: '13px',
                                      fontWeight: '600',
                                      transition: 'background-color 0.2s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                      if (!userState.loading) e.target.style.backgroundColor = '#2980b9';
                                    }}
                                    onMouseLeave={(e) => {
                                      if (!userState.loading) e.target.style.backgroundColor = '#3498db';
                                    }}
                                  >
                                    {userState.loading ? 'Generating...' : 'Generate SSO'}
                                  </button>
                                ) : (
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    <div style={{
                                      padding: '8px',
                                      backgroundColor: '#d4edda',
                                      border: '1px solid #c3e6cb',
                                      borderRadius: '4px',
                                      fontSize: '12px',
                                      color: '#155724'
                                    }}>
                                      ✅ Link generated
                                    </div>
                                    <div style={{
                                      padding: '8px',
                                      backgroundColor: '#f8f9fa',
                                      borderRadius: '4px',
                                      fontSize: '11px',
                                      fontFamily: 'monospace',
                                      wordBreak: 'break-all',
                                      color: '#495057',
                                      border: '1px solid #e9ecef'
                                    }}>
                                      {userState.loginUrl}
                                    </div>
                                    <button
                                      onClick={() => copySSOToClipboard(userState.loginUrl, workspaceUser.email)}
                                      style={{
                                        padding: '6px 12px',
                                        backgroundColor: userState.copied ? '#27ae60' : '#2c3e50',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontSize: '12px',
                                        fontWeight: '600',
                                        transition: 'background-color 0.2s ease'
                                      }}
                                      onMouseEnter={(e) => {
                                        if (!userState.copied) e.target.style.backgroundColor = '#34495e';
                                      }}
                                      onMouseLeave={(e) => {
                                        if (!userState.copied) e.target.style.backgroundColor = '#2c3e50';
                                      }}
                                    >
                                      {userState.copied ? '✓ Copied!' : '📋 Copy Link'}
                                    </button>
                                  </div>
                                )}
                                {userState.error && (
                                  <div style={{
                                    marginTop: '8px',
                                    padding: '8px',
                                    backgroundColor: '#f8d7da',
                                    border: '1px solid #f5c6cb',
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                    color: '#721c24'
                                  }}>
                                    ⚠️ {userState.error}
                                  </div>
                                )}
                                {(() => {
                                  const magicLinkState = magicLinkStates[workspaceUser.email] || {};
                                  return null;
                                })()}
                              </div>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}


        {/* Segments Section */}
        {workspaceInfo && (
          <div style={{
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            padding: '25px',
            border: '1px solid #e9ecef',
            marginTop: '30px'
          }} ref={segmentsSectionRef} id="segments">
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
              borderBottom: '2px solid #3498db',
              paddingBottom: '10px'
            }}>
              <h2 style={{
                fontSize: '20px',
                fontWeight: 'bold',
                margin: 0,
                color: '#2c3e50',
              }}>
                📁 Segments ({segments.length})
              </h2>
              {segments.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <label style={{ fontSize: '13px', color: '#495057', fontWeight: '500' }}>
                    Sort by:
                  </label>
                  <select
                    value={segmentSortKey}
                    onChange={(e) => setSegmentSortKey(e.target.value)}
                    style={{
                      padding: '6px 12px',
                      border: '1px solid #ced4da',
                      borderRadius: '4px',
                      fontSize: '13px',
                      backgroundColor: 'white',
                      cursor: 'pointer',
                      outline: 'none'
                    }}
                  >
                    <option value="systemUpdatedAt">Last Updated</option>
                    <option value="sourceCount">Source Count</option>
                    <option value="createdAt">Created At</option>
                  </select>
                </div>
              )}
            </div>

            {segmentsLoading ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#6c757d' }}>
                Loading segments...
              </div>
            ) : segmentsError ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#dc3545' }}>
                {segmentsError}
              </div>
            ) : segments.length === 0 ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#6c757d' }}>
                No segments created
              </div>
            ) : (
              <div style={{
                backgroundColor: 'white',
                borderRadius: '6px',
                overflow: 'hidden',
                border: '1px solid #e9ecef',
                maxHeight: '400px',
                overflowY: 'auto',
                overflowX: 'auto'
              }}>
                <table style={{ width: '100%', minWidth: '1600px', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#495057', fontSize: '14px', width: '250px' }}>Segment Name</th>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#495057', fontSize: '14px' }}>Source Count</th>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#495057', fontSize: '14px' }}>Last Updated</th>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#495057', fontSize: '14px' }}>Creator</th>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#495057', fontSize: '14px' }}>Created At</th>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#495057', fontSize: '14px' }}>Company Website Fill Rate</th>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#495057', fontSize: '14px' }}>Email Fill Rate</th>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#495057', fontSize: '14px' }}>Phone Fill Rate</th>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#495057', fontSize: '14px' }}>LinkedIn (Person) Fill Rate</th>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#495057', fontSize: '14px' }}>First Name Fill Rate</th>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#495057', fontSize: '14px' }}>Last Name Fill Rate</th>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#495057', fontSize: '14px' }}>LinkedIn Last Active Fill Rate</th>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#495057', fontSize: '14px' }}>Company LinkedIn Fill Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      // Sort segments based on selected sort key and direction
                      const sortedSegments = [...segments].sort((a, b) => {
                        let aValue, bValue;

                        if (segmentSortKey === 'sourceCount') {
                          aValue = a.sourceCount || 0;
                          bValue = b.sourceCount || 0;
                        } else if (segmentSortKey === 'systemUpdatedAt') {
                          aValue = a.systemUpdatedAt || 0;
                          bValue = b.systemUpdatedAt || 0;
                        } else if (segmentSortKey === 'createdAt') {
                          aValue = a.createdAt || 0;
                          bValue = b.createdAt || 0;
                        }

                        // Always sort descending (newest/highest first)
                        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
                      });

                      return sortedSegments;
                    })().map((segment, index) => (
                      <tr key={segment.segmentId || index} style={{ borderBottom: index < segments.length - 1 ? '1px solid #e9ecef' : 'none', transition: 'background-color 0.2s ease' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}>
                        <td style={{ padding: '12px 16px', color: '#212529', fontSize: '14px', maxWidth: '250px', overflowX: 'auto', whiteSpace: 'nowrap' }}>
                          <div style={{ overflowX: 'auto', paddingBottom: '4px' }}>{segment.name}</div>
                        </td>
                        <td style={{ padding: '12px 16px', color: '#3498db', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer' }} onClick={() => { setActiveSegment(segment); setShowSourcesModal(true); }}>
                          {segment.sourceCount || 0}
                        </td>
                        <td style={{ padding: '12px 16px', color: '#6c757d', fontSize: '13px' }}>
                          {segment.systemUpdatedAt ? new Date(segment.systemUpdatedAt).toLocaleString() : '-'}
                        </td>
                        <td style={{ padding: '12px 16px', color: '#6c757d', fontSize: '13px' }}>
                          <div>{segment.creator?.name || segment.creatorId || '-'}</div>
                          {segment.creator?.email && (
                            <div style={{ fontSize: '11px', color: '#adb5bd', marginTop: '2px' }}>
                              {segment.creator.email}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '12px 16px', color: '#6c757d', fontSize: '13px' }}>
                          {segment.createdAt ? new Date(segment.createdAt).toLocaleString() : '-'}
                        </td>
                        {(() => {
                          const r = segment?.fillRates?.rates || {};
                          const cell = (key) => {
                            const stKey = `${segment.segmentId}:${key}`;
                            const st = segmentFillRateStates[stKey] || {};
                            const hasRate = r[key] && typeof r[key].rate === 'number';
                            return (
                              <td style={{ padding: '12px 16px', color: '#6c757d', fontSize: '13px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  {hasRate ? <span>{formatRate(r[key])}</span> : null}
                                  {(!hasRate || st.loading) ? (
                                    <button
                                      onClick={(e) => { e.stopPropagation(); generateFillRateForSegmentField(segment, key); }}
                                      disabled={!!st.loading}
                                      style={{
                                        padding: '4px 8px',
                                        borderRadius: '6px',
                                        border: '1px solid #ced4da',
                                        backgroundColor: st.loading ? '#e9ecef' : 'white',
                                        cursor: st.loading ? 'not-allowed' : 'pointer',
                                        fontSize: '11px',
                                        fontWeight: 600,
                                        whiteSpace: 'nowrap'
                                      }}
                                    >
                                      {st.loading ? 'Generating...' : 'Generate'}
                                    </button>
                                  ) : null}
                                </div>
                                {st.error ? (
                                  <div style={{ color: '#dc3545', fontSize: '11px', marginTop: '4px', maxWidth: '220px' }}>
                                    {st.error}
                                  </div>
                                ) : null}
                              </td>
                            );
                          };

                          return (
                            <>
                              {cell('companyWebsite')}
                              {cell('email')}
                              {cell('phoneNumber')}
                              {cell('linkedinProfileUrlPerson')}
                              {cell('firstName')}
                              {cell('lastName')}
                              {cell('linkedinLastActiveDate')}
                              {cell('companyLinkedinProfile')}
                            </>
                          );
                        })()}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Enrichments Section */}
        {/* {workspaceInfo && (
          <div style={{
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            padding: '25px',
            border: '1px solid #e9ecef',
            marginTop: '30px'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
              borderBottom: '2px solid #3498db',
              paddingBottom: '10px'
            }}>
              <h2 style={{
                fontSize: '20px',
                fontWeight: 'bold',
                margin: 0,
                color: '#2c3e50',
              }}>
                Enrichments in the past 7 days ({taskRuns.length})
              </h2>
            </div>

            {taskRunsLoading ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#6c757d' }}>
                Loading enrichments...
              </div>
            ) : taskRunsError ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#dc3545' }}>
                {taskRunsError}
              </div>
            ) : taskRuns.length === 0 ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#6c757d' }}>
                No enrichments in the past 7 days
              </div>
            ) : (
              <div style={{
                backgroundColor: 'white',
                borderRadius: '6px',
                overflow: 'hidden',
                border: '1px solid #e9ecef',
                maxHeight: '400px',
                overflowY: 'auto'
              }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#495057', fontSize: '14px' }}>Segment Name</th>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#495057', fontSize: '14px' }}>Task Type</th>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#495057', fontSize: '14px' }}>Start Time</th>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#495057', fontSize: '14px' }}>Creator</th>
                    </tr>
                  </thead>
                  <tbody>
                    {taskRuns.map((run, index) => (
                      <tr key={run.taskId || index} style={{ borderBottom: index < taskRuns.length - 1 ? '1px solid #e9ecef' : 'none', transition: 'background-color 0.2s ease' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}>
                        <td style={{ padding: '12px 16px', color: '#212529', fontSize: '14px' }}>{run.segmentName || '-'}</td>
                        <td style={{ padding: '12px 16px', color: '#6c757d', fontSize: '13px' }}>
                          {run.taskType || '-'}
                        </td>
                        <td style={{ padding: '12px 16px', color: '#6c757d', fontSize: '13px' }}>
                          {run.startTime ? new Date(run.startTime).toLocaleString() : '-'}
                        </td>
                        <td style={{ padding: '12px 16px', color: '#6c757d', fontSize: '13px' }}>
                          <div>{run.creator?.name || run.creatorId || '-'}</div>
                          {run.creator?.email && (
                            <div style={{ fontSize: '11px', color: '#adb5bd', marginTop: '2px' }}>
                              {run.creator.email}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )} */}
        {/* Platform Usage Section */}
        {workspaceInfo && (
          <div
            style={{
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              padding: '25px',
              border: '1px solid #e9ecef',
              marginTop: '30px'
            }}
          >
            <h2
              style={{
                fontSize: '20px',
                fontWeight: 'bold',
                marginBottom: '20px',
                color: '#2c3e50',
                borderBottom: '2px solid #3498db',
                paddingBottom: '10px',
              }}
            >
              Platform Usage
            </h2>

            {platformUsageLoading && (
              <div style={{ color: '#6c757d' }}>Loading platform usage…</div>
            )}

            {platformUsageError && (
              <div style={{ color: '#dc3545' }}>{platformUsageError}</div>
            )}

            {platformUsage && (
              <>
                <div style={{ marginBottom: '16px' }}>
                  <strong>Total Contacts:</strong>{' '}
                  {platformUsage?.contacts?.total?.toLocaleString()}
                </div>

                {/* ICPs */}
                <div style={{ marginBottom: '20px' }}>
                  <div
                    style={{ cursor: 'pointer', fontWeight: 600 }}
                    onClick={() =>
                      setExpanded((e) => ({ ...e, icps: !e.icps }))
                    }
                  >
                    ▶ ICPs Set Up
                  </div>
                  <div style={{ marginLeft: '16px', fontSize: '14px' }}>
                    Total ICPs: {platformUsage?.icps?.totalIcps}
                  </div>
                  {expanded.icps && (
                    <ul style={{ marginTop: '10px', paddingLeft: '20px' }}>
                      {/* GOOD ICPs */}
                      {goodIcps.map((icp) => (
                        <li
                          key={icp.id}
                          style={{
                            marginBottom: '12px',
                            backgroundColor: '#ffffff',
                            border: '1px solid #e9ecef',
                            borderRadius: '8px',
                            padding: '12px',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              cursor: 'pointer'
                            }}
                            onClick={() =>
                              setExpanded((e) => ({
                                ...e,
                                icpConditions: {
                                  ...e.icpConditions,
                                  [icp.id]: !e.icpConditions[icp.id],
                                },
                              }))
                            }
                          >
                            <div style={{ fontWeight: 600, color: '#2c3e50', display: 'flex', alignItems: 'center' }}>
                              <span style={{ marginRight: '8px', transition: 'transform 0.2s', transform: expanded.icpConditions[icp.id] ? 'rotate(90deg)' : 'none' }}>▶</span>
                              <div>
                                <div>{icp.name}</div>
                                {icp.creator && (
                                  <div style={{ fontSize: '12px', fontWeight: 400, color: '#6c757d', marginTop: '2px' }}>
                                    By: {icp.creator.name} ({icp.creator.email})
                                  </div>
                                )}
                              </div>
                            </div>
                            <span style={{
                              fontSize: '11px',
                              fontWeight: 600,
                              textTransform: 'uppercase',
                              padding: '2px 8px',
                              borderRadius: '12px',
                              backgroundColor: '#d4edda',
                              color: '#155724',
                              border: '1px solid #c3e6cb'
                            }}>
                              Good
                            </span>
                          </div>

                          {expanded.icpConditions[icp.id] && (
                            <div style={{ marginTop: '12px', paddingLeft: '22px', borderTop: '1px solid #f1f3f5', paddingTop: '10px' }}>
                              <div style={{ marginBottom: '8px', fontSize: '13px', color: '#6c757d' }}>
                                <strong>Logic:</strong> {formatGroupOperator(icp.operator)}
                              </div>
                              <ul style={{ margin: 0, paddingLeft: '16px' }}>
                                {(icp.conditions || []).map((c, idx) => (
                                  <li key={idx} style={{ fontSize: '13px', marginBottom: '4px' }}>
                                    <span style={{ color: '#495057', fontWeight: 500 }}>{c.field}</span>
                                    <span style={{ color: '#adb5bd', margin: '0 6px' }}>{c.operator}</span>
                                    <span style={{ color: '#2c3e50' }}>{Array.isArray(c.values) ? c.values.join(", ") : "-"}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </li>
                      ))}

                      {/* BAD ICPs */}
                      {badIcps.map((icp) => (
                        <li
                          key={icp.id}
                          style={{
                            marginBottom: '12px',
                            backgroundColor: '#ffffff',
                            border: '1px solid #e9ecef',
                            borderRadius: '8px',
                            padding: '12px',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              cursor: 'pointer'
                            }}
                            onClick={() =>
                              setExpanded((e) => ({
                                ...e,
                                icpConditions: {
                                  ...e.icpConditions,
                                  [icp.id]: !e.icpConditions[icp.id],
                                },
                              }))
                            }
                          >
                            <div style={{ fontWeight: 600, color: '#2c3e50', display: 'flex', alignItems: 'center' }}>
                              <span style={{ marginRight: '8px', transition: 'transform 0.2s', transform: expanded.icpConditions[icp.id] ? 'rotate(90deg)' : 'none' }}>▶</span>
                              <div>
                                <div>{icp.name}</div>
                                {icp.creator && (
                                  <div style={{ fontSize: '12px', fontWeight: 400, color: '#6c757d', marginTop: '2px' }}>
                                    By: {icp.creator.name} ({icp.creator.email})
                                  </div>
                                )}
                              </div>
                            </div>
                            <span style={{
                              fontSize: '11px',
                              fontWeight: 600,
                              textTransform: 'uppercase',
                              padding: '2px 8px',
                              borderRadius: '12px',
                              backgroundColor: '#fff3cd',
                              color: '#856404',
                              border: '1px solid #ffeeba'
                            }}>
                              Incomplete
                            </span>
                          </div>

                          {expanded.icpConditions[icp.id] && (
                            <div style={{ marginTop: '12px', paddingLeft: '22px', borderTop: '1px solid #f1f3f5', paddingTop: '10px' }}>
                              <div style={{ marginBottom: '8px', fontSize: '13px', color: '#6c757d' }}>
                                <strong>Logic:</strong> {formatGroupOperator(icp.operator)}
                              </div>
                              <ul style={{ margin: 0, paddingLeft: '16px' }}>
                                {(icp.conditions || []).map((c, idx) => (
                                  <li key={idx} style={{ fontSize: '13px', marginBottom: '4px' }}>
                                    <span style={{ color: '#495057', fontWeight: 500 }}>{c.field}</span>
                                    <span style={{ color: '#adb5bd', margin: '0 6px' }}>{c.operator}</span>
                                    <span style={{ color: '#2c3e50' }}>{Array.isArray(c.values) ? c.values.join(", ") : "-"}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* Signals */}
                <div style={{ marginBottom: '20px' }}>
                  <div
                    style={{ cursor: 'pointer', fontWeight: 600 }}
                    onClick={() =>
                      setExpanded((e) => ({ ...e, signals: !e.signals }))
                    }
                  >
                    ▶ Signals Set Up
                  </div>
                  <div style={{ marginLeft: '16px', fontSize: '14px' }}>
                    Total Signals: {platformUsage?.signals?.totalSignals}
                  </div>

                  {expanded.signals && (
                    <ul style={{ marginTop: '10px', paddingLeft: '20px' }}>
                      {/* GOOD SIGNALS */}
                      {goodSignals.map((signal) => (
                        <li
                          key={signal.id}
                          style={{
                            marginBottom: '12px',
                            backgroundColor: '#ffffff',
                            border: '1px solid #e9ecef',
                            borderRadius: '8px',
                            padding: '12px',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              cursor: 'pointer'
                            }}
                            onClick={() =>
                              setExpanded((e) => ({
                                ...e,
                                signalConditions: {
                                  ...e.signalConditions,
                                  [signal.id]: !e.signalConditions[signal.id],
                                },
                              }))
                            }
                          >
                            <div style={{ fontWeight: 600, color: '#2c3e50', display: 'flex', alignItems: 'center' }}>
                              <span style={{ marginRight: '8px', transition: 'transform 0.2s', transform: expanded.signalConditions[signal.id] ? 'rotate(90deg)' : 'none' }}>▶</span>
                              {signal.name}
                            </div>
                            <span style={{
                              fontSize: '11px',
                              fontWeight: 600,
                              textTransform: 'uppercase',
                              padding: '2px 8px',
                              borderRadius: '12px',
                              backgroundColor: '#d4edda',
                              color: '#155724',
                              border: '1px solid #c3e6cb'
                            }}>
                              Good
                            </span>
                          </div>

                          {expanded.signalConditions[signal.id] && (
                            <div style={{ marginTop: '12px', paddingLeft: '22px', borderTop: '1px solid #f1f3f5', paddingTop: '10px' }}>
                              <div style={{ marginBottom: '8px', fontSize: '13px', color: '#6c757d' }}>
                                <strong>Logic:</strong> {formatGroupOperator(signal.operator)}
                              </div>
                              <ul style={{ margin: 0, paddingLeft: '16px' }}>
                                {(signal.conditions || []).map((c, idx) => (
                                  <li key={idx} style={{ fontSize: '13px', marginBottom: '4px' }}>
                                    <span style={{ color: '#495057', fontWeight: 500 }}>{c.field}</span>
                                    <span style={{ color: '#adb5bd', margin: '0 6px' }}>{c.operator}</span>
                                    <span style={{ color: '#2c3e50' }}>{Array.isArray(c.values) ? c.values.join(", ") : "-"}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </li>
                      ))}

                      {/* BAD SIGNALS */}
                      {badSignals.map((signal) => (
                        <li
                          key={signal.id}
                          style={{
                            marginBottom: '12px',
                            backgroundColor: '#ffffff',
                            border: '1px solid #e9ecef',
                            borderRadius: '8px',
                            padding: '12px',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              cursor: 'pointer'
                            }}
                            onClick={() =>
                              setExpanded((e) => ({
                                ...e,
                                signalConditions: {
                                  ...e.signalConditions,
                                  [signal.id]: !e.signalConditions[signal.id],
                                },
                              }))
                            }
                          >
                            <div style={{ fontWeight: 600, color: '#2c3e50', display: 'flex', alignItems: 'center' }}>
                              <span style={{ marginRight: '8px', transition: 'transform 0.2s', transform: expanded.signalConditions[signal.id] ? 'rotate(90deg)' : 'none' }}>▶</span>
                              {signal.name}
                            </div>
                            <span style={{
                              fontSize: '11px',
                              fontWeight: 600,
                              textTransform: 'uppercase',
                              padding: '2px 8px',
                              borderRadius: '12px',
                              backgroundColor: '#fff3cd',
                              color: '#856404',
                              border: '1px solid #ffeeba'
                            }}>
                              Incomplete
                            </span>
                          </div>

                          {expanded.signalConditions[signal.id] && (
                            <div style={{ marginTop: '12px', paddingLeft: '22px', borderTop: '1px solid #f1f3f5', paddingTop: '10px' }}>
                              <div style={{ marginBottom: '8px', fontSize: '13px', color: '#6c757d' }}>
                                <strong>Logic:</strong> {formatGroupOperator(signal.operator)}
                              </div>
                              <ul style={{ margin: 0, paddingLeft: '16px' }}>
                                {(signal.conditions || []).map((c, idx) => (
                                  <li key={idx} style={{ fontSize: '13px', marginBottom: '4px' }}>
                                    <span style={{ color: '#495057', fontWeight: 500 }}>{c.field}</span>
                                    <span style={{ color: '#adb5bd', margin: '0 6px' }}>{c.operator}</span>
                                    <span style={{ color: '#2c3e50' }}>{Array.isArray(c.values) ? c.values.join(", ") : "-"}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* Tasks */}
                <div>
                  <div
                    style={{ cursor: 'pointer', fontWeight: 600 }}
                    onClick={() =>
                      setExpanded((e) => ({ ...e, tasks: !e.tasks }))
                    }
                  >
                    ▶ Tasks Configured
                  </div>

                  <div style={{ marginLeft: '16px', fontSize: '14px' }}>
                    Total Tasks: {platformUsage?.tasks?.totalTasksMade}
                    <br />
                    Tasks Last Week: {platformUsage?.tasks?.totalTasksMadeLastWeek}
                  </div>

                  {expanded.tasks && (
                    <ul>
                      {(platformUsage?.tasks?.lastWeekTasks || []).map((task) => (
                        <li key={task.id} style={{ marginBottom: "14px" }}>
                          <strong>{task.name}</strong>
                          {task.creator && (
                            <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '2px' }}>
                              By: {task.creator.name} ({task.creator.email})
                            </div>
                          )}

                          {task.actions?.length > 0 && (
                            <ul style={{ marginTop: "6px", paddingLeft: "20px" }}>
                              {task.actions.map((action, idx) => (
                                <li key={idx} style={{ listStyleType: "circle" }}>
                                  <div>Type: {action.type}</div>
                                  {action.subType && (
                                    <div>Sub-type: {action.subType}</div>
                                  )}
                                </li>
                              ))}
                            </ul>
                          )}
                        </li>
                      ))}
                    </ul>


                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* Adoption Readiness Section */}
        {adoptionSnapshot && (
          <div
            style={{
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              padding: '25px',
              border: '1px solid #e9ecef',
              marginTop: '30px'
            }}
          >
            <h2
              style={{
                fontSize: '20px',
                fontWeight: 'bold',
                marginBottom: '20px',
                color: '#2c3e50',
                borderBottom: '2px solid #3498db',
                paddingBottom: '10px'
              }}
            >
              Adoption Readiness
            </h2>

            <div style={{ display: 'grid', gap: '16px' }}>
              {/* SETUP */}
              <div style={cardStyle}>
                <strong>Setup</strong>
                <ul style={listStyle}>
                  <li>{adoptionSnapshot.setup.contacts_available ? '✅' : '❌'} Contacts available</li>
                  <li>{adoptionSnapshot.setup.icp_configured ? '✅' : '❌'} ICP configured</li>
                  <li>{adoptionSnapshot.setup.signals_configured ? '✅' : '❌'} Signals configured</li>
                </ul>
              </div>

              {/* ACTIVATION */}
              <div style={cardStyle}>
                <strong>Enrichment</strong>
                <div style={{ marginTop: '6px' }}>
                  {adoptionSnapshot.activation.enrichment_run.ran
                    ? `✅ Enrichment ran (${adoptionSnapshot.activation.enrichment_run.count_last_7_days} runs last 7 days)`
                    : '❌ Enrichment not run for the last 14 days'}
                </div>
              </div>

              {/* AUTOMATION */}
              <div style={cardStyle}>
                <strong>Workflow launched</strong>
                <div style={{ marginTop: '6px' }}>
                  {adoptionSnapshot.automation.workflow_automated
                    ? '✅ Workflow launched'
                    : '❌ No workflow launched'}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Lifecycle Adoption Score & Next Best Action Section */}
        {adoptionSnapshot && platformUsage && (
          <div
            style={{
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              padding: '25px',
              border: '1px solid #e9ecef',
              marginTop: '30px'
            }}
          >
            <h2
              style={{
                fontSize: '20px',
                fontWeight: 'bold',
                marginBottom: '20px',
                color: '#2c3e50',
                borderBottom: '2px solid #3498db',
                paddingBottom: '10px'
              }}
            >
              Lifecycle Adoption & Recommendations
            </h2>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              {/* Lifecycle Adoption Score - Left Side */}
              <div
                style={{
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  padding: '20px',
                  border: '1px solid #e9ecef',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center'
                }}
              >
                <h3
                  style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#495057',
                    marginBottom: '20px',
                    textAlign: 'center'
                  }}
                >
                  Lifecycle adoption score
                </h3>

                {lifecycleScoreLoading ? (
                  <div style={{ color: '#6c757d' }}>Calculating...</div>
                ) : lifecycleScore && lifecycleScore.score !== undefined ? (
                  <>
                    {/* Circular Progress Indicator */}
                    <div style={{ position: 'relative', width: '120px', height: '120px', marginBottom: '15px' }}>
                      <svg width="120" height="120" style={{ transform: 'rotate(-90deg)' }}>
                        {/* Background circle */}
                        <circle
                          cx="60"
                          cy="60"
                          r="50"
                          fill="none"
                          stroke="#e9ecef"
                          strokeWidth="8"
                        />
                        {/* Progress circle */}
                        <circle
                          cx="60"
                          cy="60"
                          r="50"
                          fill="none"
                          stroke={getScoreColor(lifecycleScore.meaning)}
                          strokeWidth="8"
                          strokeDasharray={`${2 * Math.PI * 50}`}
                          strokeDashoffset={`${2 * Math.PI * 50 * (1 - lifecycleScore.score / 100)}`}
                          strokeLinecap="round"
                        />
                      </svg>
                      {/* Score text in center */}
                      <div
                        style={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          textAlign: 'center'
                        }}
                      >
                        <div
                          style={{
                            fontSize: '32px',
                            fontWeight: 'bold',
                            color: '#2c3e50',
                            lineHeight: '1'
                          }}
                        >
                          {lifecycleScore.score}
                        </div>
                      </div>
                    </div>

                    {/* Range text */}
                    <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '15px' }}>
                      0–100
                    </div>

                    {/* Status tag */}
                    <div
                      style={{
                        backgroundColor: getScoreBackgroundColor(lifecycleScore.meaning),
                        color: getScoreColor(lifecycleScore.meaning),
                        padding: '6px 12px',
                        borderRadius: '20px',
                        fontSize: '13px',
                        fontWeight: '500',
                        border: `1px solid ${getScoreColor(lifecycleScore.meaning)}`
                      }}
                    >
                      {lifecycleScore.meaning}
                    </div>
                  </>
                ) : (
                  <div style={{ color: '#6c757d' }}>Unable to calculate score</div>
                )}
              </div>

              {/* Next Best Action - Right Side */}
              <div
                style={{
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  padding: '20px',
                  border: '1px solid #e9ecef',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <h3
                  style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#495057',
                    marginBottom: '15px'
                  }}
                >
                  Next best action
                </h3>

                {lifecycleScoreLoading ? (
                  <div style={{ color: '#6c757d' }}>Calculating...</div>
                ) : nextBestAction ? (
                  <div
                    style={{
                      fontSize: '14px',
                      color: '#2c3e50',
                      lineHeight: '1.6',
                      padding: '12px',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '6px',
                      border: '1px solid #e9ecef'
                    }}
                  >
                    {nextBestAction}
                  </div>
                ) : (
                  <div style={{ color: '#6c757d' }}>Unable to determine next action</div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!workspaceInfo && !error && !loading && (
          <Empty description="Select a search type and enter a value to view workspace information" style={{ margin: '48px 0' }} />
        )}

        {/* Add Credits / Overdraft Modal */}
        {workspaceInfo && (
          <ResponsiveModal
            open={showAddCreditsModal}
            onCancel={closeAddCreditsModal}
            width={640}
            title="Add credits / Overdrafting"
          >
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message={
                <div style={{ fontSize: 13, lineHeight: 1.5 }}>
                  <div>If you expect to collect money later, raise overdraft. Do not grant bonus credits.</div>
                  <div>If the customer already paid, choose Purchased and paste the Stripe payment or customer link.</div>
                  <div>If you are giving credits with no money expected, choose Bonus credits.</div>
                </div>
              }
            />

            {addCreditsSuccess && (
              <Alert type="success" showIcon message={addCreditsSuccess} style={{ marginBottom: 16 }} />
            )}
            {addCreditsError && (
              <Alert type="error" showIcon message={addCreditsError} style={{ marginBottom: 16 }} />
            )}

            <div style={{ marginBottom: 16 }}>
              <Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 12 }}>
                Workspace
              </Text>
              <Text code>
                {workspaceInfo.accountSlug} ({workspaceInfo.environment})
              </Text>
            </div>

            <Segmented
              block
              value={creditsModalMode}
              onChange={(mode) => {
                setCreditsModalMode(mode);
                setAddCreditsError('');
                if (mode === 'purchased' && !creditDescription) {
                  setCreditDescription('Purchased');
                }
              }}
              options={[
                { label: 'Purchased', value: 'purchased' },
                { label: 'Overdraft', value: 'overdraft' },
              ]}
              style={{ marginBottom: 20 }}
            />

            {creditsModalMode === 'overdraft' ? (
              <Form layout="vertical" requiredMark={false}>
                <Form.Item
                  label="Overdraft limit"
                  extra={`Set this to 0 to remove overdrafting. Maximum ${MAX_OVERDRAFT_LIMIT.toLocaleString()}.`}
                >
                  {workspaceInfo.creditsSnapshot?.inOverdraft && (
                    <Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 12 }}>
                      Currently using {workspaceInfo.creditsSnapshot.overdraftUsed.toLocaleString()} of{' '}
                      {currentOverdraftLimit.toLocaleString()}.
                    </Text>
                  )}
                  <Space wrap>
                    <InputNumber
                      min={0}
                      max={MAX_OVERDRAFT_LIMIT}
                      value={overdraftLimitInput === '' ? null : Number(overdraftLimitInput)}
                      onChange={(v) => {
                        setOverdraftLimitInput(v == null ? '' : String(v));
                        setResetOverdraftToDefault(false);
                      }}
                      style={{ width: 160 }}
                    />
                    <Button
                      onClick={() => {
                        setOverdraftLimitInput(String(formulaOverdraftLimit));
                        setResetOverdraftToDefault(true);
                      }}
                      disabled={addingCredits}
                    >
                      Reset to Default
                    </Button>
                  </Space>
                </Form.Item>
              </Form>
            ) : (
              <Form layout="vertical" requiredMark={false}>
                <Form.Item label="Grant Reason" required>
                  <Radio.Group
                    value={creditDescription}
                    onChange={(e) => {
                      setCreditDescription(e.target.value);
                      setBonusReason('');
                      setSrivatsanApproved('no');
                      setStripePaymentLink('');
                    }}
                    optionType="button"
                    buttonStyle="solid"
                    options={[
                      { label: 'Purchased', value: 'Purchased' },
                      { label: 'Bonus credits', value: 'Bonus credits' },
                    ]}
                  />
                </Form.Item>

                {creditDescription === 'Bonus credits' && (
                  <>
                    <Form.Item
                      label="Why are we granting this bonus?"
                      required
                    >
                      <Input.TextArea
                        value={bonusReason}
                        onChange={(e) => setBonusReason(e.target.value)}
                        placeholder="Explain why these credits are complimentary, with no money expected back."
                        rows={3}
                      />
                    </Form.Item>
                    <Form.Item
                      label="Is it approved by Srivatsan?"
                      required
                      help={srivatsanApproved !== 'yes' ? 'Save is only allowed when this is Yes.' : undefined}
                      validateStatus={srivatsanApproved !== 'yes' ? 'error' : undefined}
                    >
                      <Radio.Group
                        value={srivatsanApproved}
                        onChange={(e) => setSrivatsanApproved(e.target.value)}
                        options={[
                          { label: 'Yes', value: 'yes' },
                          { label: 'No', value: 'no' },
                        ]}
                      />
                    </Form.Item>
                  </>
                )}

                {creditDescription === 'Purchased' && (
                  <Form.Item
                    label="Stripe payment or customer link"
                    required
                    extra="Paste a Dashboard payment link (pi_/pr_) or customer page URL (cus_)."
                  >
                    <Input
                      type="url"
                      value={stripePaymentLink}
                      onChange={(e) => setStripePaymentLink(e.target.value)}
                      placeholder="https://dashboard.stripe.com/.../customers/cus_… or …/payments/pi_…"
                    />
                  </Form.Item>
                )}

                {grantTypeSelected && (
                  <>
                    <Form.Item label="Credits to Add" required>
                      <InputNumber
                        min={1}
                        value={creditsToAdd === '' ? null : Number(creditsToAdd)}
                        onChange={(v) => setCreditsToAdd(v == null ? '' : String(v))}
                        placeholder="Enter credits amount"
                        style={{ width: '100%' }}
                      />
                    </Form.Item>

                    <Form.Item
                      label="Expiry Date (optional)"
                      extra="Presets set the date from today. Clear the field for no expiry."
                    >
                      <Space direction="vertical" size={8} style={{ width: '100%' }}>
                        <Space wrap size={8}>
                          {CREDITS_EXPIRY_PRESETS.map((p) => (
                            <Button
                              key={p.months}
                              size="small"
                              type={
                                creditsExpiry === expiryDateMonthsFromToday(p.months)
                                  ? 'primary'
                                  : 'default'
                              }
                              onClick={() => setCreditsExpiry(expiryDateMonthsFromToday(p.months))}
                            >
                              {p.label}
                            </Button>
                          ))}
                          {creditsExpiry ? (
                            <Button size="small" type="link" onClick={() => setCreditsExpiry('')}>
                              Clear
                            </Button>
                          ) : null}
                        </Space>
                        <Input
                          type="date"
                          value={creditsExpiry}
                          onChange={(e) => setCreditsExpiry(e.target.value)}
                        />
                      </Space>
                    </Form.Item>

                    <Form.Item label="Grant Type" required>
                      <Radio.Group
                        value={creditsGrantType}
                        onChange={(e) => setCreditsGrantType(e.target.value)}
                        options={[
                          { label: 'All at once', value: 'all-at-once' },
                          {
                            label: 'Granted in chunks based on consumption',
                            value: 'consumption-based',
                          },
                        ]}
                      />
                    </Form.Item>
                  </>
                )}
              </Form>
            )}

            <div className="wi-modal-actions">
              <Button onClick={closeAddCreditsModal} disabled={addingCredits}>
                Cancel
              </Button>
              <Button
                type="primary"
                onClick={handleSaveCreditsModal}
                loading={addingCredits}
                disabled={!canSaveCreditsModal}
              >
                Save
              </Button>
            </div>
          </ResponsiveModal>
        )}

        {/* Deduct Credits Modal */}
        {workspaceInfo && (
          <ResponsiveModal
            open={showDeductCreditsModal}
            onCancel={() => setShowDeductCreditsModal(false)}
            width={500}
            title="Deduct Credits"
          >

                {deductCreditsSuccess && (
                  <div style={{
                    padding: '12px',
                    backgroundColor: theme.name === 'dark' ? 'rgba(40, 167, 69, 0.2)' : '#d4edda',
                    border: `1px solid ${theme.success}`,
                    borderRadius: '4px',
                    color: theme.success,
                    marginBottom: '15px',
                    fontSize: '14px'
                  }}>
                    ✅ {deductCreditsSuccess}
                  </div>
                )}

                {deductCreditsError && (
                  <div style={{
                    padding: '12px',
                    backgroundColor: theme.name === 'dark' ? 'rgba(220, 53, 69, 0.2)' : '#f8d7da',
                    border: `1px solid ${theme.danger}`,
                    borderRadius: '4px',
                    color: theme.danger,
                    marginBottom: '15px',
                    fontSize: '14px'
                  }}>
                    ⚠️ {deductCreditsError}
                  </div>
                )}

                <div style={{ marginBottom: '20px' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: '600',
                    color: theme.text,
                    fontSize: '14px'
                  }}>
                    Workspace
                  </label>
                  <div style={{
                    padding: '10px',
                    backgroundColor: theme.tableHeader,
                    borderRadius: '4px',
                    fontSize: '14px',
                    color: theme.textSecondary,
                    fontFamily: 'monospace'
                  }}>
                    {workspaceInfo.accountSlug} ({workspaceInfo.environment})
                  </div>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: '600',
                    color: theme.text,
                    fontSize: '14px'
                  }}>
                    Credits to Deduct
                  </label>
                  <input
                    type="number"
                    value={creditsToDeduct}
                    onChange={(e) => setCreditsToDeduct(e.target.value)}
                    placeholder="Enter credits amount"
                    min="1"
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: `1px solid ${theme.inputBorder}`,
                      borderRadius: '4px',
                      fontSize: '14px',
                      boxSizing: 'border-box',
                      backgroundColor: theme.inputBackground,
                      color: theme.text
                    }}
                  />
                </div>

                <div className="wi-modal-actions">
                  <button
                    onClick={() => setShowDeductCreditsModal(false)}
                    disabled={deductingCredits}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#6c757d',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: deductingCredits ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: '600',
                      transition: 'background-color 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (!deductingCredits) e.target.style.backgroundColor = '#5a6268';
                    }}
                    onMouseLeave={(e) => {
                      if (!deductingCredits) e.target.style.backgroundColor = '#6c757d';
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeductCredits}
                    disabled={deductingCredits}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: deductingCredits ? '#95a5a6' : '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: deductingCredits ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: '600',
                      transition: 'background-color 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (!deductingCredits) e.target.style.backgroundColor = '#c82333';
                    }}
                    onMouseLeave={(e) => {
                      if (!deductingCredits) e.target.style.backgroundColor = '#dc3545';
                    }}
                  >
                    {deductingCredits ? 'Deducting...' : 'Deduct Credits'}
                  </button>
                </div>
          </ResponsiveModal>
        )}

        {/* Magic Link Confirmation Modal */}
        <MagicLinkConfirmationModal
          isOpen={showMagicLinkModal}
          email={magicLinkModalEmail}
          onConfirm={handleMagicLinkConfirm}
          onCancel={handleMagicLinkCancel}
        />

        {/* Change Subscription Modal */}
        {workspaceInfo && (
          <ResponsiveModal
            open={showChangeSubscriptionModal}
            onCancel={() => {
              setShowChangeSubscriptionModal(false);
              setStripeSubscriptionLink('');
              setSelectedUserEmailForSubscription('');
              setSubscriptionLinkError('');
              setChangeSubscriptionError('');
              setChangeSubscriptionSuccess('');
            }}
            width={600}
            title="Change Subscription"
          >

                {changeSubscriptionSuccess && (
                  <div style={{
                    padding: '12px',
                    backgroundColor: theme.name === 'dark' ? 'rgba(40, 167, 69, 0.2)' : '#d4edda',
                    border: `1px solid ${theme.success}`,
                    borderRadius: '4px',
                    color: theme.success,
                    marginBottom: '15px',
                    fontSize: '14px'
                  }}>
                    ✅ {changeSubscriptionSuccess}
                  </div>
                )}

                {changeSubscriptionError && (
                  <div style={{
                    padding: '12px',
                    backgroundColor: theme.name === 'dark' ? 'rgba(220, 53, 69, 0.2)' : '#f8d7da',
                    border: `1px solid ${theme.danger}`,
                    borderRadius: '4px',
                    color: theme.danger,
                    marginBottom: '15px',
                    fontSize: '14px'
                  }}>
                    ⚠️ {changeSubscriptionError}
                  </div>
                )}

                <div style={{ marginBottom: '20px' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: '600',
                    color: theme.text,
                    fontSize: '14px'
                  }}>
                    Workspace
                  </label>
                  <div style={{
                    padding: '10px',
                    backgroundColor: theme.tableHeader,
                    borderRadius: '4px',
                    fontSize: '14px',
                    color: theme.textSecondary,
                    fontFamily: 'monospace'
                  }}>
                    {workspaceInfo.accountSlug} ({workspaceInfo.environment})
                  </div>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: '600',
                    color: theme.text,
                    fontSize: '14px'
                  }}>
                    Change subscription on request of
                  </label>
                  <select
                    value={selectedUserEmailForSubscription}
                    onChange={(e) => {
                      setSelectedUserEmailForSubscription(e.target.value);
                      setChangeSubscriptionError('');
                    }}
                    disabled={loadingUsers || workspaceUsers.length === 0}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: `1px solid ${theme.inputBorder}`,
                      borderRadius: '4px',
                      fontSize: '14px',
                      outline: 'none',
                      backgroundColor: loadingUsers || workspaceUsers.length === 0 ? theme.tableHeader : theme.inputBackground,
                      cursor: loadingUsers || workspaceUsers.length === 0 ? 'not-allowed' : 'pointer',
                      fontWeight: '500',
                      boxSizing: 'border-box',
                      color: loadingUsers || workspaceUsers.length === 0 ? theme.textMuted : theme.text
                    }}
                  >
                    <option value="">
                      {loadingUsers ? 'Loading users...' : workspaceUsers.length === 0 ? 'No users available' : '-- Select a user --'}
                    </option>
                    {workspaceUsers.map(user => (
                      <option key={user.email} value={user.email}>
                        {user.name} ({user.email})
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: '600',
                    color: theme.text,
                    fontSize: '14px'
                  }}>
                    Stripe Subscription Link
                  </label>
                  <input
                    type="text"
                    value={stripeSubscriptionLink}
                    onChange={(e) => {
                      setStripeSubscriptionLink(e.target.value);
                      setSubscriptionLinkError('');
                      setChangeSubscriptionError('');
                    }}
                    placeholder="https://dashboard.stripe.com/acct_xxx/subscriptions/sub_xxx"
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: subscriptionLinkError ? `1px solid ${theme.danger}` : `1px solid ${theme.inputBorder}`,
                      borderRadius: '4px',
                      fontSize: '14px',
                      boxSizing: 'border-box',
                      fontFamily: 'monospace',
                      backgroundColor: theme.inputBackground,
                      color: theme.text
                    }}
                  />
                  {subscriptionLinkError && (
                    <div style={{
                      marginTop: '8px',
                      padding: '8px',
                      backgroundColor: theme.name === 'dark' ? 'rgba(220, 53, 69, 0.2)' : '#f8d7da',
                      border: `1px solid ${theme.danger}`,
                      borderRadius: '4px',
                      fontSize: '12px',
                      color: theme.danger
                    }}>
                      ⚠️ {subscriptionLinkError}
                    </div>
                  )}
                  <div style={{
                    marginTop: '8px',
                    padding: '8px',
                    backgroundColor: theme.name === 'dark' ? 'rgba(77, 171, 247, 0.15)' : '#e7f3ff',
                    border: `1px solid ${theme.primary}`,
                    borderRadius: '4px',
                    fontSize: '12px',
                    color: theme.primary
                  }}>
                    <strong>Format:</strong><br />
                    Production: https://dashboard.stripe.com/acct_xxx/subscriptions/sub_xxx<br />
                    Staging: https://dashboard.stripe.com/acct_xxx/test/subscriptions/sub_xxx
                  </div>
                </div>

                <div className="wi-modal-actions">
                  <button
                    onClick={() => {
                      setShowChangeSubscriptionModal(false);
                      setStripeSubscriptionLink('');
                      setSelectedUserEmailForSubscription('');
                      setSubscriptionLinkError('');
                      setChangeSubscriptionError('');
                      setChangeSubscriptionSuccess('');
                    }}
                    disabled={changingSubscription}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#6c757d',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: changingSubscription ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: '600',
                      transition: 'background-color 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (!changingSubscription) e.target.style.backgroundColor = '#5a6268';
                    }}
                    onMouseLeave={(e) => {
                      if (!changingSubscription) e.target.style.backgroundColor = '#6c757d';
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleChangeSubscription}
                    disabled={changingSubscription || !selectedUserEmailForSubscription}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: changingSubscription || !selectedUserEmailForSubscription ? '#95a5a6' : '#9b59b6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: changingSubscription || !selectedUserEmailForSubscription ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: '600',
                      transition: 'background-color 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (!changingSubscription && selectedUserEmailForSubscription) e.target.style.backgroundColor = '#8e44ad';
                    }}
                    onMouseLeave={(e) => {
                      if (!changingSubscription && selectedUserEmailForSubscription) e.target.style.backgroundColor = '#9b59b6';
                    }}
                  >
                    {changingSubscription ? 'Changing...' : 'Change Subscription'}
                  </button>
                </div>
          </ResponsiveModal>
        )}

        {/* Sources Breakdown Modal */}
        {showSourcesModal && activeSegment && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: theme.cardBackground,
              borderRadius: '8px',
              padding: '25px',
              width: '450px',
              maxWidth: '90%',
              boxShadow: `0 10px 25px ${theme.cardShadow}`,
              position: 'relative',
              border: `1px solid ${theme.border}`
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '20px',
                borderBottom: `2px solid ${theme.primary}`,
                paddingBottom: '10px'
              }}>
                <h3 style={{ margin: 0, fontSize: '18px', color: theme.text }}>
                  Sources for {activeSegment.name}
                </h3>
                <button
                  onClick={() => setShowSourcesModal(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '24px',
                    cursor: 'pointer',
                    color: theme.textMuted,
                    lineHeight: '1'
                  }}
                >
                  &times;
                </button>
              </div>

              <div style={{ maxHeight: '300px', overflowY: 'auto', marginBottom: '20px' }}>
                {(() => {
                  const sourceCounts = (activeSegment.sources || []).reduce((acc, s) => {
                    const type = s.attributes?.sourceType || s.sourceType || 'Unknown';
                    acc[type] = (acc[type] || 0) + 1;
                    return acc;
                  }, {});

                  const entries = Object.entries(sourceCounts);
                  if (entries.length === 0) return <div style={{ textAlign: 'center', padding: '20px', color: theme.textMuted, fontStyle: 'italic' }}>No source details available</div>;

                  return entries.sort((a, b) => b[1] - a[1]).map(([type, count], idx) => (
                    <div key={type} style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px 15px',
                      borderBottom: idx < entries.length - 1 ? `1px solid ${theme.borderLight}` : 'none',
                      backgroundColor: idx % 2 === 0 ? theme.tableHeader : theme.cardBackground,
                      borderRadius: '4px'
                    }}>
                      <span style={{ fontWeight: 500, color: theme.text, fontSize: '14px' }}>{type}</span>
                      <span style={{
                        backgroundColor: theme.primary,
                        color: 'white',
                        padding: '2px 10px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: 'bold'
                      }}>{count}</span>
                    </div>
                  ));
                })()}
              </div>

              <div style={{ textAlign: 'right' }}>
                <button
                  onClick={() => setShowSourcesModal(false)}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: theme.primary,
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '14px',
                    transition: 'background-color 0.2s ease'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = theme.primaryHover}
                  onMouseLeave={(e) => e.target.style.backgroundColor = theme.primary}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkspaceInformation;

